import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { loadErrorOrNull, findOrThrow, isNotFoundError, loadErrorMessage, swallowedFailure } from "shared/loadOrError";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import type {
  ClientRecord,
  ClientModuleRecord,
  ClientToolRecord,
  ClientModuleFormData,
  ClientToolFormData,
  ClientContactRecord,
  ClientContactFormData,
  ClientSummaryRecord,
  ClientHealthRecord,
  ClientHealthStatus,
  CommunicationRecord,
  AuditLogRecord,
  DevOpsEventRecord,
  ClientActivityItem,
  ClientDocumentRecord,
  ClientOnboardingStepKey,
  DevOpsLinkLiveStatus,
  ClientSentiment,
  RelationshipItem,
  EmailMessageRow,
  ClientDeliverable,
  ClientHealthHistoryRecord,
  ClientInvoiceRecord,
  ClientMeetingRecord,
} from "../clients.types";
import {
  CLIENT_ONBOARDING_STEPS,
  healthTierScore,
  invoiceAmount,
} from "../clients.types";
// The FULL governed record (kind + governedRepo + aliases + assignable + …),
// not `clients.types`' deliberately-partial picker view: linking a client's
// free-text key to its Module 360 needs the governed columns. One definition,
// owned by the modules feature area.
import {
  governedSlugOf,
  parseJsonArrayColumn,
  type ModuleCatalogRecord,
} from "../../modules/modules.types";
import { canWriteClients, isClientsAdmin } from "../rbac";
import {
  addWeeks,
  currentWeekStart,
  weekStartOf,
  type WhiteboardCellRecord as ClientWhiteboardCell,
  type WhiteboardRowRecord as ClientWhiteboardRow,
} from "../../whiteboard/whiteboard.types";
import {
  subscribeClientEvents,
  subscribeClientMeetingEvents,
  subscribeClientWorkEvents,
  type ClientMeetingAnalysedEvent,
  type ClientServiceEvent,
  type ClientWorkEvent,
} from "../clientsRealtime";
import { coalesce } from "shared/coalesce";
import {
  normalizePipeline,
  type DevOpsConnection,
  type DevOpsLink,
  type DevOpsLinkFormData,
  type DevOpsLinkType,
  type DevOpsPipelineApiRecord,
  type DevOpsProject,
  type DevOpsRepo,
  type DevOpsPipeline,
  type DevOpsSubscriptionCreatePayload,
} from "../../devops/devops.types";
import {
  isClientInstanceRunning,
  CLIENT_INSTANCE_ROLE_ORDER,
  type ClientInstance,
  type ClientInstanceStatus,
  type InstanceActionType,
} from "../../infrastructure/infrastructure.types";
import { canManageInfra } from "../../infrastructure/rbac";
import { runInstanceAction } from "../../infrastructure/instanceActions";
import { followHandOff } from "../../infrastructure/InstanceActions/InstanceActionsStore";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { exportToCsv, type ColumnConfig } from "shared/common/utils/export";
import { isSessionEnded, SessionEndedError, sessionMark } from "stores/sessionGuard";
import { bearerHeaders, saveBlobInSession } from "shared/sessionDownload";
import { openActionItems } from "../../meetingIntelligence/actionItemClose";
import type ClientOverviewStore from "./ClientOverviewStore";

/**
 * A Feathers `find` result as the ServiceStores surface it: either the
 * paginated envelope `{ data, total }` or — for custom/non-paginated derived
 * services — the bare array of rows.
 */
type FindResult<T> = { total?: number; data?: T[] } | T[] | null | undefined;

/** How long a burst of work events is gathered into one re-count of the Deliverables chip. */
export const TASK_COUNTS_LIVE_DEBOUNCE_MS = 250;

/** Client ids are GUIDs: the database hands them back upper-case, a request may carry lower. */
const sameClientId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/**
 * Normalise a {@link FindResult} into a plain array of rows. Replaces the
 * former list-unwrap helper: a paginated KnexService returns `{ data: [...] }`;
 * a custom/derived service returns the bare array.
 */
function rowsOf<T>(result: FindResult<T>): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * The subset of the RAW Feathers client a link write needs. Link writes go
 * through `rootStore.app.service(path)` first and fall back to the registered
 * ServiceStore wrapper, because the wrapper SWALLOWS a rejection — it records
 * it on `errorOnCreate`/`errorOnPatch` and resolves null — and a refusal the
 * server named (a duplicate link is a Conflict; a repo/pipeline link with no
 * entity id is refused) has to reach the dialog in the server's words instead
 * of reading as a save. Same seam as ReadAiSettingsStore's `rawService`.
 */
interface RawWriteService {
  create(data: object): Promise<unknown>;
  patch(id: string | number, data: object): Promise<unknown>;
}

/**
 * The independently loaded data sections of Client 360. Each sub-load records
 * its own outcome under its key in `sectionErrors`, so a tab can tell "this
 * data failed to load" from "this client has none" — the two used to render
 * the same empty sentence (walkthrough 5×5 §4.3).
 */
export const CLIENT_SECTION_KEYS = [
  "modules",
  "tools",
  "contacts",
  "activity",
  "documents",
  "summary",
  "health",
  "healthHistory",
  "billing",
  "meetings",
  "whiteboard",
  "devopsLinks",
  "instances",
  "happiness",
  "relationship",
  "emailMessages",
] as const;

export type ClientSectionKey = (typeof CLIENT_SECTION_KEYS)[number];

function emptySectionState<T>(value: T): Record<ClientSectionKey, T> {
  return Object.fromEntries(CLIENT_SECTION_KEYS.map((k) => [k, value])) as Record<
    ClientSectionKey,
    T
  >;
}

/** The message an error carries, for a dialog alert — never blank. */
function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * The Client 360 link form: the shared create-link shape plus the governed
 * module slug this surface can set. Held here rather than on the shared form
 * type because the slug is optional attribution, not part of a link's identity.
 */
export type ClientDevOpsLinkForm = DevOpsLinkFormData & {
  governedSlug?: string | null;
};

/** One Module-select option in the link dialogs. `slug` is the option value. */
export interface DevOpsLinkModuleOption {
  slug: string;
  displayName: string;
  moduleKey: string;
}

// ---------------------------------------------------------------------------
// Tab-level search/filter/sort vocabulary (Comms / Documents / Deliverables).
// All three tabs are CLIENT-side over their loaded, clientId-scoped lists —
// the shared TableSort helpers give them the same click semantics as the
// Meetings list (see components/TableSort/tableSort.ts).
// ---------------------------------------------------------------------------

/** Sortable columns of the Comms email-threads table. */
export type CommsSortKey = "subject" | "format" | "sentAt";
const COMMS_DESC_FIRST: ReadonlySet<CommsSortKey> = new Set(["sentAt"]);

/** Sortable columns of the Documents table. */
export type DocumentSortKey = "fileName" | "category" | "fileSize" | "uploadedAt";
const DOCS_DESC_FIRST: ReadonlySet<DocumentSortKey> = new Set([
  "fileSize",
  "uploadedAt",
]);

/** Deliverables Type filter — the merged list interleaves two entity kinds. */
export type DeliverableTypeFilter = "all" | "module" | "tool";
/** Sortable columns of the Deliverables table (all text — A→Z first). */
export type DeliverableSortKey = "kind" | "name" | "status";
const DELIVERABLES_DESC_FIRST: ReadonlySet<DeliverableSortKey> = new Set();

/** Page cap on the Comms email-thread load (the tab shows the latest N). */
export const EMAIL_MESSAGES_LIMIT = 100;

/** Points plotted by the Overview health sparkline (most recent verdicts). */
export const HEALTH_HISTORY_LIMIT = 30;

/** Page cap on the Meetings tab roster (the tab shows the latest N). */
export const CLIENT_MEETINGS_LIMIT = 100;

/** Page cap on the Billing tab's invoice load (newest first). */
export const CLIENT_INVOICES_LIMIT = 200;

/** Page cap on the Documents tab load (newest first). */
export const CLIENT_DOCUMENTS_LIMIT = 200;

/** Page cap applied to EACH of the three activity-timeline sources. */
export const ACTIVITY_SOURCE_LIMIT = 100;

/**
 * The Activity tab's empty state.
 *
 * It says ATTRIBUTED, not "recorded", and the difference is the whole point.
 * Every source of this timeline is keyed by `clientId`, so an empty list means
 * "HQ has nothing filed under this client" — never "nothing happened on this
 * client's work". Since the API's attribution became kind-aware (2026-09-03)
 * that gap is real rather than theoretical: a DevOps event on a project two
 * clients share, which none of this client's links claims, is deliberately
 * stamped with NO client instead of being guessed onto one, and a feed keyed
 * by clientId cannot see it. "No activity recorded for this client yet" told
 * the operator something HQ does not know.
 */
export const ACTIVITY_EMPTY_NOTE = "No activity attributed to this client yet";

/**
 * The caveat rendered whenever {@link ClientDetailStore.devopsAttributionCaveat}
 * holds — under an empty timeline AND under a populated one, because a
 * populated list makes the same over-claim more quietly.
 */
export const DEVOPS_ATTRIBUTION_NOTE =
  "DevOps events are listed only where HQ could attribute them to this client. " +
  "On a project shared with another client, an event that none of this client's " +
  "links claims is left unattributed rather than guessed onto a customer, and " +
  "does not appear here.";

/** Normalised document category — matches the "other" the Category chip renders. */
function docCategory(doc: ClientDocumentRecord): string {
  return doc.category || "other";
}

// ---------------------------------------------------------------------------
// Client 360 — per-tab CSV export
// ---------------------------------------------------------------------------

/**
 * The Client 360 tabs that can produce a CSV, keyed by the tab NAME.
 *
 * The name is the identity: `<Tabs useRouterState>` writes the active tab's
 * `name` into `?tabName=`, which is what the header reads to know what to
 * export. These constants are also spread into ClientDetail.Body's `tabs`
 * array so a renamed tab cannot silently disconnect its export.
 */
export const CLIENT_TAB_CONTACTS = "Contacts";
export const CLIENT_TAB_MEETINGS = "Meetings";
export const CLIENT_TAB_DOCUMENTS = "Documents";
export const CLIENT_TAB_DELIVERABLES = "Modules & Tools";
export const CLIENT_TAB_BILLING = "Billing";

/**
 * A tab's export payload: what the header hands to the toolkit.
 * `slug` becomes part of the file name.
 */
export interface ClientTabExport {
  tab: string;
  slug: string;
  columns: ColumnConfig[];
  rows: Array<Record<string, unknown>>;
}

/** ISO date-only, tolerating both date strings and full timestamps. */
const isoDate = (value: unknown): string =>
  value === null || value === undefined || value === "" ? "" : String(value).slice(0, 10);

/**
 * No `format` on any column across the Client 360 exports, deliberately —
 * matching the invoices precedent: the toolkit's currency/number/date
 * formatters emit locale strings ("$1,234.00", "8/1/2026") that a spreadsheet
 * re-imports as text. Raw numbers and ISO dates are what a person can pivot on.
 */
export const CLIENT_CONTACT_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "name", label: "Name" },
  { key: "title", label: "Title" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "isPrimary", label: "Primary" },
  { key: "notes", label: "Notes" },
];

export const CLIENT_MEETING_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "title", label: "Title" },
  { key: "startTime", label: "When" },
  { key: "mappingStatus", label: "Mapping" },
  { key: "actionItemCount", label: "Action Items" },
  { key: "processed", label: "Processed" },
];

export const CLIENT_DOCUMENT_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "fileName", label: "File" },
  { key: "category", label: "Category" },
  { key: "fileSize", label: "Size (bytes)" },
  { key: "uploadedAt", label: "Uploaded" },
];

export const CLIENT_DELIVERABLE_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "kind", label: "Kind" },
  { key: "name", label: "Name" },
  { key: "key", label: "Key" },
  { key: "status", label: "Status" },
  { key: "deployable", label: "Deployable" },
  { key: "openTasks", label: "Open Work Items" },
  { key: "totalTasks", label: "Total Work Items" },
];

export const CLIENT_INVOICE_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "invoiceNumber", label: "Invoice" },
  { key: "issueDate", label: "Issued" },
  { key: "dueDate", label: "Due" },
  { key: "status", label: "Status" },
  { key: "total", label: "Total" },
];

/** Contacts tab rows → export records. PURE. */
export function buildContactExportRows(
  contacts: ClientContactRecord[]
): Array<Record<string, unknown>> {
  return contacts.map((c) => ({
    name: c.name ?? "",
    title: c.title ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    isPrimary: c.isPrimary ? "yes" : "no",
    notes: c.notes ?? "",
  }));
}

/** Meetings tab rows → export records. PURE. */
export function buildMeetingExportRows(
  meetings: ClientMeetingRecord[]
): Array<Record<string, unknown>> {
  return meetings.map((m) => ({
    title: m.title ?? "",
    // Full ISO timestamp, not a locale string: the time of day matters for a
    // meeting and a spreadsheet parses ISO reliably. Unscheduled meetings fall
    // back to when they were ingested, exactly as the tab's ordering does.
    startTime: m.startTime || m.createdAt || "",
    mappingStatus: m.mappingStatus ?? "",
    actionItemCount: openActionItems(m),
    processed: m.processed ? "yes" : "no",
  }));
}

/** Documents tab rows → export records. PURE. */
export function buildDocumentExportRows(
  documents: ClientDocumentRecord[]
): Array<Record<string, unknown>> {
  return documents.map((d) => ({
    fileName: d.originalFileName || d.fileName || "",
    category: docCategory(d),
    // Raw bytes, not the humanised "1.2 MB" the table renders — a spreadsheet
    // can format bytes, but it cannot sort "900 KB" against "1.2 MB".
    fileSize: typeof d.fileSize === "number" ? d.fileSize : "",
    uploadedAt: d.uploadedAt || d.createdAt || "",
  }));
}

/**
 * Whether a live event's row is a tab's: `here` (the payload says it belongs
 * to the open client), `elsewhere` (it says it belongs to another — a row
 * moved away), or `unknown` (the payload does not carry the key at all, as a
 * write made with `$select` does not).
 */
export type LiveMembership = "here" | "elsewhere" | "unknown";

/**
 * One live CRUD event applied to a tab's own copy of its rows. PURE.
 *
 * `membership` is the caller's answer (it knows the key: the open client, the
 * lane a week note sits on, the ledger's subject):
 *  - `elsewhere`: the row is dropped if this tab lists it (moved to another
 *    client, its lane gone);
 *  - `here`: a removal or a soft delete (a `patched` event carrying
 *    `deletedAt`, then `removed`) drops it; otherwise it is merged into the
 *    listed row, or inserted when absent — created here, or moved here from
 *    another client;
 *  - `unknown`: the payload says nothing about whose it is, so it can only
 *    update a row this tab already lists (or drop it, on a removal).
 * Ids compare as strings (the ledger's are numbers, the client-* tables'
 * uuids). Returns the SAME array when nothing changed, so another client's
 * event re-renders nothing.
 */
export function applyLiveEvent<T extends { id: string | number }>(
  rows: T[],
  event: Pick<ClientServiceEvent, "action" | "record">,
  membership: LiveMembership,
  insertAt: "start" | "end" = "end"
): T[] {
  const { action, record } = event;
  if (record.id === undefined || record.id === null) return rows;
  const id = String(record.id);
  const index = rows.findIndex((r) => String(r.id) === id);
  if (membership === "elsewhere" || action === "removed" || record.deletedAt) {
    return index === -1 ? rows : rows.filter((_, i) => i !== index);
  }
  const incoming = record as unknown as T;
  if (index === -1) {
    if (membership !== "here") return rows;
    return insertAt === "start" ? [incoming, ...rows] : [...rows, incoming];
  }
  const next = rows.slice();
  next[index] = { ...rows[index], ...incoming };
  return next;
}

/** Modules & Tools tab rows → export records. PURE. */
export function buildDeliverableExportRows(
  deliverables: ClientDeliverable[],
  taskCounts: Record<string, { open: number; total: number }>
): Array<Record<string, unknown>> {
  return deliverables.map((d) => {
    // Work items are attributed to MODULES only (read_ai_assignments.moduleId),
    // so a tool exports empty cells rather than a fabricated 0.
    const counts = d.kind === "module" ? taskCounts[d.record.id] : undefined;
    return {
      kind: d.kind,
      name: d.kind === "module" ? d.record.displayName : d.record.name,
      key: d.kind === "module" ? d.record.moduleKey : d.record.toolKey,
      status: d.record.status ?? "",
      deployable:
        d.kind === "module" ? (d.record.isDeployable ? "yes" : "no") : "",
      openTasks: counts ? counts.open : "",
      totalTasks: counts ? counts.total : "",
    };
  });
}

/** Billing tab rows → export records. PURE. */
export function buildClientInvoiceExportRows(
  invoices: ClientInvoiceRecord[]
): Array<Record<string, unknown>> {
  return invoices.map((i) => ({
    invoiceNumber: i.invoiceNumber ?? "",
    issueDate: isoDate(i.issueDate),
    dueDate: isoDate(i.dueDate),
    status: i.status ?? "",
    total: invoiceAmount(i.total),
  }));
}

/**
 * View store for the Client detail hub.
 *
 * Plain class with `makeAutoObservable` (route ViewStores never extend
 * ViewStore). Reads the registered `@intrasync/stores` ServiceStores (see the
 * domain configs) by hand — `get`/`find`/`create`/`patch`/`remove` — across the
 * `clients`, `client-modules`/`client-tools`/`client-contacts`,
 * DevOps, activity, documents, rollup, and instance services.
 *
 * These child collections are all clientId-SCOPED finds (re-queried whenever the
 * open client changes) and are patched in place by the realtime handler, so the
 * store keeps each as a local observable populated from the `find()` RESPONSE —
 * not the cross-query entity cache.
 */
class ClientDetailStore {
  rootStore: RootStore;

  clientId: string | null = null;
  loading = false;

  client: ClientRecord | null = null;

  /**
   * Why the CLIENT RECORD itself could not be fetched (network, server, auth),
   * or null. Distinct from {@link notFound}: a failed fetch must never read as
   * "Client not found". Cleared at the start of every load.
   */
  loadError: string | null = null;
  /** The fetch succeeded and there is no such client (404 / empty answer). */
  notFound = false;

  /**
   * Per-section load outcome: the message of the section's last failed load, or
   * null when it loaded (possibly empty) or has not run. A tab renders its empty
   * sentence ONLY when its section is null here. Load STATE only — the data
   * itself stays where it was.
   */
  sectionErrors: Record<ClientSectionKey, string | null> = emptySectionState<string | null>(null);
  modules: ClientModuleRecord[] = [];
  tools: ClientToolRecord[] = [];

  modulesLoading = false;
  toolsLoading = false;

  // ---- Module form state (ClientModulesPanel) ----
  moduleForm: ClientModuleFormData = {};
  editingModuleId: string | null = null;
  moduleSaving = false;

  // Org-wide module vocabulary (module-catalog service) feeding the dialog's
  // moduleKey picker. Loaded lazily on first dialog open; best-effort — an
  // empty catalog just leaves the picker free-text. A FAILED load is said so
  // (`moduleCatalogError`) and retried on the next open, never latched as
  // "loaded, and there are no modules".
  moduleCatalog: ModuleCatalogRecord[] = [];
  moduleCatalogError: string | null = null;

  /** The catalog read currently in flight, shared by every concurrent caller. */
  private moduleCatalogInFlight: Promise<void> | null = null;
  private moduleCatalogLoaded = false;

  // ---- Per-module work rollup (Deliverables tab "Tasks" chip) ----
  // open/total `read_ai_assignments` counts keyed by moduleId. Projects are
  // gone: a distributed meeting action item IS the unit of tracked work, and
  // `read_ai_assignments.moduleId` is the ONLY place work is attributed to a
  // module/tool/integration. This rollup is that attribution, surfaced.
  moduleTaskCounts: Record<string, { open: number; total: number }> = {};
  // Live (#3820) — plumbing, not state (excluded from MobX below). The
  // assignments the last count read, as strings: the assignment notices name
  // an assignment, never its client, so they are matched against these.
  private taskCountAssignmentIds = new Set<string>();
  /** Bumped by every count read: an older answer never lands over a newer. */
  private taskCountsSeq = 0;
  private workEventsDisposer: (() => void) | null = null;
  /** One re-count per burst of work events (a close pass accepts several at once). */
  private readonly taskCountsSoon = coalesce(() => void this.loadModuleTaskCounts(), TASK_COUNTS_LIVE_DEBOUNCE_MS);

  // ---- Tool form state (ClientToolsPanel) ----
  toolForm: ClientToolFormData = {};
  editingToolId: string | null = null;
  toolSaving = false;

  // ---- DevOps links state (DevOpsLinksTab) ----
  devopsLinks: DevOpsLink[] = [];
  devopsLinksLoading = false;

  // Connections + live Azure read-only data used by the create-link picker.
  devopsConnections: DevOpsConnection[] = [];
  devopsProjects: DevOpsProject[] = [];
  devopsRepos: DevOpsRepo[] = [];
  devopsPipelines: DevOpsPipeline[] = [];
  devopsProjectsLoading = false;
  devopsEntitiesLoading = false;

  devopsLinkForm: ClientDevOpsLinkForm = {};
  devopsLinkSaving = false;
  /** The row the Edit dialog is changing; null while the wizard is creating. */
  editingDevOpsLinkId: string | null = null;
  /**
   * The server's refusal (or the transport failure) from the last link write,
   * in its own words — shown in the open dialog. null once a write succeeds
   * or a dialog is (re)opened.
   */
  devopsLinkError: string | null = null;
  /**
   * Why the last Remove on the DevOps links table was refused, in the API's
   * words — shown above the table. `devopsLinkError` belongs to the add/edit
   * dialogs; this one is for a row action, so it shows where the row was.
   */
  devopsLinkActionError: string | null = null;
  /** Why the last Delete / enable-disable on Modules & Tools was refused. */
  deliverableActionError: string | null = null;
  /** Why the last contact Delete or primary-star change was refused. */
  contactActionError: string | null = null;
  /**
   * Why the open Add/Edit Module, Tool or Contact dialog's last Save was
   * refused, in the API's words — shown once, inside that dialog, which stays
   * open. Cleared when a dialog is (re)opened or a save starts.
   */
  moduleFormError: string | null = null;
  toolFormError: string | null = null;
  contactFormError: string | null = null;
  /**
   * Connect-wizard outcome: per-event-type subscription failures from the last
   * link save. The link itself is NEVER rolled back on a subscription failure —
   * these are surfaced as a dismissible warning in the tab instead.
   */
  devopsLinkSubscriptionErrors: string[] = [];

  // ---- Contacts state (ContactsTab) ----
  contacts: ClientContactRecord[] = [];
  contactsLoading = false;
  contactForm: ClientContactFormData = {};
  editingContactId: string | null = null;
  contactSaving = false;

  // ---- Activity timeline state (ActivityTab) ----
  communications: CommunicationRecord[] = [];
  auditEntries: AuditLogRecord[] = [];
  devopsEvents: DevOpsEventRecord[] = [];
  activityLoading = false;
  /** Number of timeline pages currently shown ("Show more" increments). */
  activityPage = 1;
  // Quick-add note form (creates a communications row, sourceType='note').
  noteSubject = "";
  noteSummary = "";
  noteSaving = false;

  // ---- Documents state (DocumentsTab) ----
  documents: ClientDocumentRecord[] = [];
  documentsLoading = false;
  documentUploading = false;
  documentError: string | null = null;

  // ---- Rollups + health (Overview / header) ----
  summary: ClientSummaryRecord | null = null;
  health: ClientHealthRecord | null = null;
  /** Observed health verdicts, OLDEST FIRST — the overview sparkline series. */
  healthHistory: ClientHealthHistoryRecord[] = [];

  // ---- Billing (BillingTab) ----
  // This client's invoices, read-only. Billing is an OPTIONAL module: the tab
  // only exists on an instance the API reports as billing-configured, so both
  // the capability answer and the rows are held here.
  invoices: ClientInvoiceRecord[] = [];
  invoicesLoading = false;
  /** null until the capability report has been read; then true/false. */
  billingEnabled: boolean | null = null;

  // ---- Meetings (MeetingsTab) ----
  // This client's ingested Read.ai meetings (read-ai-meetings, clientId-scoped).
  meetings: ClientMeetingRecord[] = [];
  meetingsLoading = false;

  // ---- Whiteboard (WhiteboardTab) ----
  // This client's whiteboard lanes + their recent weekly cells (clientId-scoped
  // finds against whiteboard-rows / whiteboard-cells). The other half of the
  // two-way tie: the Whiteboard route links lanes here, and this tab links back
  // via whiteboardStore.focusClient() + goTo("Whiteboard").
  whiteboardRows: ClientWhiteboardRow[] = [];
  whiteboardCells: ClientWhiteboardCell[] = [];
  whiteboardLoading = false;

  // ---- Onboarding stepper (OverviewTab) ----
  onboardingSaving = false;
  /** Why the last stage change was refused — shown by the stepper, never read as saved. */
  onboardingError: string | null = null;

  // ---- Archive / restore ----
  archiveBusy = false;
  /** Why the Archive confirm's last Archive was refused — shown in that dialog, which stays open. */
  archiveError: string | null = null;

  // ---- Live status per DevOps link, keyed by link id (DevOpsLinksTab) ----
  devopsLinkStatuses: Record<string, DevOpsLinkLiveStatus> = {};

  // Realtime subscription disposers (not observable — plain plumbing).
  private realtimeDisposer: (() => void) | null = null;
  private meetingEventsDisposer: (() => void) | null = null;

  // ---- Client instances state (InstancesTab) ----
  // READ-ONLY (Phase 4): this client's deployed containers (api + web) and their
  // live status/image. start/stop/deploy actions are Phase 5 — see the seam in
  // InstancesTab.tsx. Instance rows carry only status/image metadata, no secrets.
  instances: ClientInstance[] = [];
  instancesLoading = false;
  // Phase 5: in-flight start/stop/restart/deploy/rollback ids + last error.
  instanceActioningIds: string[] = [];
  instanceActionError: string | null = null;

  // ---- Meeting Intelligence (happiness + relationship ledger) -------------
  // Both are enrichments off the read-ai domain: client-sentiment rolls the
  // per-meeting happiness scores into one signal; relationship-items is the
  // expectation/need/commitment ledger for this client. Each degrades silently
  // (an empty/absent service simply hides its chip/tab content).
  happiness: ClientSentiment | null = null;
  relationshipItems: RelationshipItem[] = [];
  relationshipLoading = false;

  // ---- Comms uploads (transcripts + Outlook email threads) ----------------
  // Uploaded email threads attributed to this client (email-messages service),
  // plus the in-flight state for the two raw-body upload endpoints.
  emailMessages: EmailMessageRow[] = [];
  emailMessagesLoading = false;
  transcriptUploading = false;
  emailUploading = false;
  uploadError: string | null = null;
  /** Human note after a successful upload (e.g. "Transcript queued for analysis"). */
  uploadNotice: string | null = null;

  // ---- Tab view state: search / filter / sort -----------------------------
  // Namespaced per tab (comms* / docs* / deliverables*). Entity data stays in
  // the loaded collections above — these fields only drive the computed
  // filtered+sorted views the Comms / Documents / Deliverables tabs render.
  commsSearch = "";
  commsSortKey: CommsSortKey = "sentAt";
  commsSortDir: SortDir = "desc";

  docsSearch = "";
  /** "all" or a normalised category from {@link documentCategoryOptions}. */
  docsCategoryFilter = "all";
  docsSortKey: DocumentSortKey = "uploadedAt";
  docsSortDir: SortDir = "desc";

  deliverablesTypeFilter: DeliverableTypeFilter = "all";
  // "kind" asc reproduces the tab's historical default ordering: Modules
  // before Tools, name-sorted within each kind (sortRows is stable over the
  // already name-sorted `deliverables`).
  deliverablesSortKey: DeliverableSortKey = "kind";
  deliverablesSortDir: SortDir = "asc";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "taskCountAssignmentIds" | "taskCountsSeq" | "workEventsDisposer" | "taskCountsSoon">(this, {
      rootStore: false,
      taskCountAssignmentIds: false,
      taskCountsSeq: false,
      workEventsDisposer: false,
      // A function with a `cancel` on it: wrapped as an action it would lose that.
      taskCountsSoon: false,
    });
  }

  // ---- Registered ServiceStore handles (read by hand) ---------------------
  // Each getter maps a feathers service path to its `rootStore.<camel>Store`.

  private get clientsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("clients") ?? this.rootStore.clientsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get clientModulesService(): ServiceStoreType | null {
    return (this.rootStore.clientModulesStore as ServiceStoreType | null) ?? null;
  }
  private get moduleCatalogService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("module-catalog") ?? this.rootStore.moduleCatalogStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /**
   * `read-ai-assignments` — distributed meeting action items. Rows carry
   * `moduleId`, so this service backs the per-module work rollup that replaced
   * the removed `project-tasks` counts.
   */
  private get assignmentsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("read-ai-assignments") ?? this.rootStore.readAiAssignmentsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get clientToolsService(): ServiceStoreType | null {
    return (this.rootStore.clientToolsStore as ServiceStoreType | null) ?? null;
  }
  private get clientContactsService(): ServiceStoreType | null {
    return (this.rootStore.clientContactsStore as ServiceStoreType | null) ?? null;
  }
  private get devopsLinksService(): ServiceStoreType | null {
    return (this.rootStore.devopsLinksStore as ServiceStoreType | null) ?? null;
  }
  /** The RAW Feathers service for a write path — null in a test/offline root. */
  private rawService(path: string): RawWriteService | null {
    const app = this.rootStore.app as
      | { service?: (p: string) => RawWriteService }
      | undefined;
    return app?.service?.(path) ?? null;
  }
  private get devopsConnectionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get devopsProjectsService(): ServiceStoreType | null {
    return (this.rootStore.devopsProjectsStore as ServiceStoreType | null) ?? null;
  }
  private get devopsReposService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-repos") ?? this.rootStore.devopsReposStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get devopsPipelinesService(): ServiceStoreType | null {
    return (this.rootStore.devopsPipelinesStore as ServiceStoreType | null) ?? null;
  }
  private get devopsSubscriptionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-subscriptions") ?? this.rootStore.devopsSubscriptionsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get communicationsService(): ServiceStoreType | null {
    return (this.rootStore.communicationsStore as ServiceStoreType | null) ?? null;
  }
  private get auditLogService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("audit-log") ?? this.rootStore.auditLogStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get devopsEventsService(): ServiceStoreType | null {
    return (this.rootStore.devopsEventsStore as ServiceStoreType | null) ?? null;
  }
  private get documentsService(): ServiceStoreType | null {
    return (this.rootStore.documentsStore as ServiceStoreType | null) ?? null;
  }
  private get clientSummaryService(): ServiceStoreType | null {
    return (this.rootStore.clientSummaryStore as ServiceStoreType | null) ?? null;
  }
  private get clientHealthService(): ServiceStoreType | null {
    return (this.rootStore.clientHealthStore as ServiceStoreType | null) ?? null;
  }
  /** Append-only `client-health-history` (clients.stores.ts) — staff READ. */
  private get clientHealthHistoryService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("client-health-history") ?? this.rootStore.clientHealthHistoryStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /** `invoices` (billing.stores.ts) — READ ONLY from the Client 360 side. */
  private get invoicesService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("invoices") ?? this.rootStore.invoicesStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /**
   * The billing module's capability store (views/billing). Owns the "is billing
   * configured on THIS instance" answer; `load()` caches, so calling it per
   * client load costs one request for the whole session.
   */
  private get billingCapabilities(): {
    load: () => Promise<{ enabled: boolean }>;
  } | null {
    return (
      (this.rootStore as unknown as {
        billingCapabilitiesViewStore?: { load: () => Promise<{ enabled: boolean }> };
      }).billingCapabilitiesViewStore ?? null
    );
  }
  /** `read-ai-meetings` read service (meetingIntelligence.stores.ts). */
  private get readAiMeetingsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("read-ai-meetings") ?? this.rootStore.readAiMeetingsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get clientInstancesService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("client-instances") ?? this.rootStore.clientInstancesStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /** Create-only `instance-actions` ServiceStore (infrastructure.stores.ts). */
  private get instanceActionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("instance-actions") ?? this.rootStore.instanceActionsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /** `client-sentiment` rollup (read-ai meetingIntelligence.stores.ts). */
  private get clientSentimentService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("client-sentiment") ?? this.rootStore.clientSentimentStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /** `relationship-items` ledger (read-ai meetingIntelligence.stores.ts). */
  private get relationshipItemsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("relationship-items") ?? this.rootStore.relationshipItemsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /** `email-messages` read service (uploaded Outlook threads for this client). */
  private get emailMessagesService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("email-messages") ?? this.rootStore.emailMessagesStore) as unknown as ServiceStoreType | null) ?? null;
  }
  /** `whiteboard-rows` / `whiteboard-cells` (whiteboard.stores.ts). */
  private get whiteboardRowsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("whiteboard-rows") ?? this.rootStore.whiteboardRowsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get whiteboardCellsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("whiteboard-cells") ?? this.rootStore.whiteboardCellsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  setClientId(id: string) {
    this.clientId = id;
    this.subscribeRealtime();
  }

  // ---- Derived ----

  /** The Overview read's store, pinned to the same client by the ClientDetail route. */
  private get overviewStore(): ClientOverviewStore | null {
    const store = (this.rootStore as unknown as { clientOverviewViewStore?: ClientOverviewStore })
      .clientOverviewViewStore;
    return store && store.clientId && this.clientId && store.clientId.toLowerCase() === this.clientId.toLowerCase()
      ? store
      : null;
  }

  /**
   * May this person change THIS client? The API's answer (the client write
   * gate, #2613: with HQ_CLIENT_WRITE_GATE on, a non-admin changes only the
   * clients they are assigned to), read with the Overview. null while unknown —
   * not read yet, or the read failed — and then the role alone decides, because
   * the API refuses what it does not allow whatever this page shows.
   */
  get clientEditAllowed(): boolean | null {
    return this.overviewStore?.canEditClient ?? null;
  }

  /** The API's sentence for why this person may not change this client, or null. */
  get clientEditRefusal(): string | null {
    return this.overviewStore?.editRefusal ?? null;
  }

  /**
   * operator+, AND allowed for this client — whether write controls render
   * across the Client 360 tabs.
   */
  get canWrite(): boolean {
    return canWriteClients(this.rootStore) && this.clientEditAllowed !== false;
  }

  /** admin only — restore archived clients / read archived rows. */
  get isAdminTier(): boolean {
    return isClientsAdmin(this.rootStore);
  }

  get isArchived(): boolean {
    return !!this.client?.deletedAt;
  }

  /** Normalised green/yellow/red — the API serialises `health`. */
  get healthStatus(): ClientHealthStatus | null {
    const h = this.health;
    if (!h) return null;
    return h.health || h.status || h.score || null;
  }

  /** Human-readable health reasons, tolerating the `signals` alternate. */
  get healthReasons(): string[] {
    const h = this.health;
    if (!h) return [];
    return h.reasons || h.signals || [];
  }

  get primaryContact(): ClientContactRecord | null {
    return this.contacts.find((c) => c.isPrimary) || null;
  }

  /** Index of the client's current onboarding step in the ordered list. */
  get onboardingStepIndex(): number {
    const step = this.client?.onboardingStep;
    if (!step) return -1;
    return CLIENT_ONBOARDING_STEPS.findIndex((s) => s.key === step);
  }

  get activeModuleCount(): number {
    return this.modules.filter((m) => m.status === "enabled").length;
  }

  get deployableModuleCount(): number {
    return this.modules.filter((m) => m.isDeployable).length;
  }

  get activeToolCount(): number {
    return this.tools.filter((t) => t.status === "active").length;
  }

  /**
   * The unified Modules & Tools ("Deliverables") table rows: modules first
   * (sorted by displayName), then tools (sorted by name), each tagged with its
   * kind and a list key unique across both id spaces. Copies before sorting so
   * the underlying observable arrays are never mutated.
   */
  get deliverables(): ClientDeliverable[] {
    const modules: ClientDeliverable[] = this.modules
      .slice()
      .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }))
      .map((m) => ({ kind: "module", key: `module-${m.id}`, record: m }));
    const tools: ClientDeliverable[] = this.tools
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
      .map((t) => ({ kind: "tool", key: `tool-${t.id}`, record: t }));
    return [...modules, ...tools];
  }

  /**
   * Free-text client key -> the governed catalog entry it names, or null.
   *
   * `client_modules.moduleKey` / `client_tools.toolKey` are deliberately free
   * text with no FK (a `freeSolo` picker keeps uncatalogued keys valid), so this
   * is the bridge that lets a client's row link to the Module 360 page.
   *
   * DELIBERATELY CONSERVATIVE — exact, case-insensitive matches against the
   * catalog's own spellings (moduleKey, displayName, central aliases) and
   * NOTHING ELSE. It does not normalise, because the API owns exactly one
   * normaliser (`module-resolver.ts` `normalizeModuleKey`, byte-identical to
   * Module-Coordinator's) and a second forked copy on the web would silently
   * change what a mirrored HQ resolves — the precise seam the central catalog
   * exists to close. A key this misses simply gets no link, which is the honest
   * outcome: an unlinked row means "no governed entry answers to this spelling",
   * and a wrong link would be worse than none.
   *
   * Reads the assignable-filtered `moduleCatalog`, so `core`/`libraries`/
   * `payroll` never resolve here even though they have pages.
   */
  private get governedBySpelling(): Map<string, ModuleCatalogRecord | null> {
    // `null` is the AMBIGUOUS marker: a spelling claimed by two different
    // entries resolves to nothing at all rather than to whichever was indexed
    // first — the same "multiple hits are a hard stop, not a fall-through"
    // posture as the API's match ladder. Once ambiguous, always ambiguous.
    const index = new Map<string, ModuleCatalogRecord | null>();
    const add = (spelling: string | null | undefined, row: ModuleCatalogRecord) => {
      const k = (spelling ?? "").trim().toLowerCase();
      if (!k) return;
      if (!index.has(k)) {
        index.set(k, row);
        return;
      }
      const existing = index.get(k);
      if (existing && existing.id !== row.id) index.set(k, null);
    };
    for (const row of this.moduleCatalog) {
      if (!row.governedRepo) continue; // legacy vocabulary row — nothing to link to
      add(row.moduleKey, row);
      add(row.displayName, row);
      for (const alias of parseJsonArrayColumn(row.aliases)) add(alias, row);
    }
    return index;
  }

  /** The governed slug a client's free-text key names, or null when nothing does. */
  governedSlugForKey(key: string | null | undefined): string | null {
    const hit = this.governedBySpelling.get((key ?? "").trim().toLowerCase());
    return hit ? governedSlugOf(hit) : null;
  }

  /** Open the Module 360 for a client row's key. No-op when it does not resolve. */
  openGovernedModule(key: string | null | undefined) {
    const slug = this.governedSlugForKey(key);
    if (!slug) return;
    void this.rootStore.routerStore?.goTo("ModuleDetail", { slug });
  }

  // ---- Tab views: filtered + sorted rows (Comms / Documents / Deliverables)

  /** Comms rows matching the free-text search (subject or sender). */
  get filteredEmailMessages(): EmailMessageRow[] {
    const q = this.commsSearch.trim().toLowerCase();
    if (!q) return this.emailMessages;
    return this.emailMessages.filter(
      (m) =>
        m.subject?.toLowerCase().includes(q) ||
        m.fromName?.toLowerCase().includes(q) ||
        m.fromEmail?.toLowerCase().includes(q)
    );
  }

  /** The rows the Comms table renders: search, then the active column sort. */
  get sortedEmailMessages(): EmailMessageRow[] {
    const key = this.commsSortKey;
    const valueOf = (m: EmailMessageRow): SortValue => {
      switch (key) {
        case "subject":
          return textValue(m.subject);
        case "format":
          return textValue(m.format);
        case "sentAt":
          return dateValue(m.sentAt || m.createdAt);
      }
    };
    return sortRows(this.filteredEmailMessages, valueOf, this.commsSortDir);
  }

  /** True when the load hit the page cap — older threads exist but are not shown. */
  get emailMessagesCapped(): boolean {
    return this.emailMessages.length >= EMAIL_MESSAGES_LIMIT;
  }

  /** Distinct (normalised) categories in the loaded documents, for the filter. */
  get documentCategoryOptions(): string[] {
    const categories = new Set<string>();
    for (const doc of this.documents) categories.add(docCategory(doc));
    return [...categories].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }

  /** Documents matching the filename search and the Category filter. */
  get filteredDocuments(): ClientDocumentRecord[] {
    let result = this.documents;
    if (this.docsCategoryFilter !== "all") {
      result = result.filter((d) => docCategory(d) === this.docsCategoryFilter);
    }
    const q = this.docsSearch.trim().toLowerCase();
    if (q) {
      result = result.filter((d) =>
        (d.originalFileName || d.fileName)?.toLowerCase().includes(q)
      );
    }
    return result;
  }

  /** True when the document load hit its page cap — older files are not shown. */
  get documentsCapped(): boolean {
    return this.documents.length >= CLIENT_DOCUMENTS_LIMIT;
  }

  /** The rows the Documents table renders: filters, then the column sort. */
  get sortedDocuments(): ClientDocumentRecord[] {
    const key = this.docsSortKey;
    const valueOf = (d: ClientDocumentRecord): SortValue => {
      switch (key) {
        case "fileName":
          return textValue(d.originalFileName || d.fileName);
        case "category":
          return docCategory(d);
        case "fileSize":
          return d.fileSize ?? null;
        case "uploadedAt":
          return dateValue(d.uploadedAt || d.createdAt);
      }
    };
    return sortRows(this.filteredDocuments, valueOf, this.docsSortDir);
  }

  /** Deliverables narrowed by the Type filter (All / Modules / Tools). */
  get filteredDeliverables(): ClientDeliverable[] {
    if (this.deliverablesTypeFilter === "all") return this.deliverables;
    return this.deliverables.filter((d) => d.kind === this.deliverablesTypeFilter);
  }

  /** The rows the Deliverables table renders: Type filter, then column sort. */
  get sortedDeliverables(): ClientDeliverable[] {
    const key = this.deliverablesSortKey;
    const valueOf = (d: ClientDeliverable): SortValue => {
      switch (key) {
        case "kind":
          return d.kind;
        case "name":
          return textValue(
            d.kind === "module" ? d.record.displayName : d.record.name
          );
        case "status":
          return textValue(d.record.status);
      }
    };
    return sortRows(this.filteredDeliverables, valueOf, this.deliverablesSortDir);
  }

  // ---- Tab view state setters ---------------------------------------------

  setCommsSearch(q: string) {
    this.commsSearch = q;
  }

  /** Comms header click — Sent is desc-first (newest on top), text A→Z. */
  setCommsSort(key: CommsSortKey) {
    const next = nextSort(this.commsSortKey, this.commsSortDir, key, COMMS_DESC_FIRST);
    this.commsSortKey = next.key;
    this.commsSortDir = next.dir;
  }

  setDocsSearch(q: string) {
    this.docsSearch = q;
  }

  setDocsCategoryFilter(category: string) {
    this.docsCategoryFilter = category;
  }

  /** Documents header click — Size and Uploaded are desc-first, text A→Z. */
  setDocsSort(key: DocumentSortKey) {
    const next = nextSort(this.docsSortKey, this.docsSortDir, key, DOCS_DESC_FIRST);
    this.docsSortKey = next.key;
    this.docsSortDir = next.dir;
  }

  setDeliverablesTypeFilter(filter: DeliverableTypeFilter) {
    this.deliverablesTypeFilter = filter;
  }

  /** Deliverables header click — all columns are text, A→Z first. */
  setDeliverablesSort(key: DeliverableSortKey) {
    const next = nextSort(
      this.deliverablesSortKey,
      this.deliverablesSortDir,
      key,
      DELIVERABLES_DESC_FIRST
    );
    this.deliverablesSortKey = next.key;
    this.deliverablesSortDir = next.dir;
  }

  // ---- Per-tab CSV export --------------------------------------------------

  /**
   * The open tab's NAME, as `<Tabs useRouterState>` records it in `?tabName=`.
   * Empty until the Tabs component has written the param, which it does on
   * mount for the first tab (Overview) — an empty value simply has no export.
   */
  get activeTabName(): string {
    const params = this.rootStore.routerStore?.queryParams as
      | Record<string, string | undefined>
      | undefined;
    return String(params?.tabName ?? "");
  }

  /**
   * The last tab opened in each Client 360 SECTION (clientTabGroups.ts), so
   * re-selecting a section returns the user to where they were in it. Pure UI
   * memory, never entity data. Kept here rather than in the page: the page
   * re-mounts after each navigation, and a component ref was wiped every time,
   * so every section always reopened on its first tab.
   */
  lastTabBySection: Record<string, string> = {};

  rememberSectionTab(section: string, tabName: string) {
    if (this.lastTabBySection[section] !== tabName) {
      this.lastTabBySection = { ...this.lastTabBySection, [section]: tabName };
    }
  }

  /**
   * What the header's Export CSV button writes for the tab that is open, or
   * null when this tab is not a list (Overview, Relationship, DevOps Links,
   * Instances, Comms, Activity — none of them is a table a person would pivot
   * on).
   *
   * Every payload is the rows ON SCREEN, in screen order: the tab's own search,
   * filters and column sort already applied. Nothing here refetches.
   */
  get tabExport(): ClientTabExport | null {
    switch (this.activeTabName) {
      case CLIENT_TAB_CONTACTS:
        return {
          tab: CLIENT_TAB_CONTACTS,
          slug: "contacts",
          columns: CLIENT_CONTACT_EXPORT_COLUMNS,
          rows: buildContactExportRows(this.contacts),
        };
      case CLIENT_TAB_MEETINGS:
        return {
          tab: CLIENT_TAB_MEETINGS,
          slug: "meetings",
          columns: CLIENT_MEETING_EXPORT_COLUMNS,
          rows: buildMeetingExportRows(this.sortedMeetings),
        };
      case CLIENT_TAB_DOCUMENTS:
        return {
          tab: CLIENT_TAB_DOCUMENTS,
          slug: "documents",
          columns: CLIENT_DOCUMENT_EXPORT_COLUMNS,
          rows: buildDocumentExportRows(this.sortedDocuments),
        };
      case CLIENT_TAB_DELIVERABLES:
        return {
          tab: CLIENT_TAB_DELIVERABLES,
          slug: "modules-tools",
          columns: CLIENT_DELIVERABLE_EXPORT_COLUMNS,
          rows: buildDeliverableExportRows(
            this.sortedDeliverables,
            this.moduleTaskCounts
          ),
        };
      case CLIENT_TAB_BILLING:
        return {
          tab: CLIENT_TAB_BILLING,
          slug: "invoices",
          columns: CLIENT_INVOICE_EXPORT_COLUMNS,
          rows: buildClientInvoiceExportRows(this.sortedInvoices),
        };
      default:
        return null;
    }
  }

  /** False on a tab with no export, and on an empty table (the toolkit throws). */
  get canExportTab(): boolean {
    return (this.tabExport?.rows.length ?? 0) > 0;
  }

  /**
   * Download the open tab as `<client-code>-<tab>-<date>.csv`. Returns false
   * instead of throwing when there is nothing to export, so the header can stay
   * a plain onClick.
   */
  exportTabCsv(): boolean {
    const payload = this.tabExport;
    if (!payload || payload.rows.length === 0) return false;
    const who = (this.client?.code || this.client?.name || "client")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    try {
      exportToCsv(payload.rows, payload.columns, {
        fileName: `${who}-${payload.slug}-${new Date().toISOString().slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("client 360: CSV export failed", err);
      return false;
    }
  }

  // ---- Loading ----

  async loadClientDetail() {
    if (!this.clientId) return;
    const isInitialLoad = !this.client || this.client.id !== this.clientId;
    if (isInitialLoad) this.loading = true;
    this.loadError = null;
    this.notFound = false;
    let client: ClientRecord | undefined;
    try {
      const service = this.clientsService;
      if (!service) throw new Error("The clients service is not available.");
      // Admins may open archived clients — pass the admin-gated soft-delete
      // escape hatch so the get does not 404 on a deletedAt row.
      // `throwOnError`: the ServiceStore otherwise SWALLOWS a failed get
      // (records errorOnGet, resolves undefined) and a network failure would
      // render as "Client not found".
      client = (await service.get(this.clientId, {
        ...(this.isAdminTier ? { query: { includeDeleted: true } } : {}),
        throwOnError: true,
      } as never)) as unknown as ClientRecord | undefined;
      if (!client) {
        const swallowed = (service as unknown as { errorOnGet?: { message?: unknown } | null })
          .errorOnGet;
        if (swallowed) throw swallowed;
      }
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (isNotFoundError(err)) {
          this.client = null;
          this.notFound = true;
        } else if (isInitialLoad) {
          // Nothing to show for this client — say why instead of "not found".
          this.client = null;
          this.loadError = loadErrorMessage(err, "This client could not be loaded.");
        }
        // A failed REFRESH of an already-loaded client keeps what is on screen.
        this.loading = false;
      });
      console.error("Failed to load client detail:", err);
      return;
    }
    runInAction(() => {
      this.client = client ?? null;
      this.notFound = !client;
      this.loading = false;
    });
    if (!client) return;
    try {
      // Fan out the dependent collections. Fire-and-forget (no waterfall):
      // each records its own outcome in `sectionErrors`.
      this.loadModules();
      this.loadTools();
      // Needed on RENDER, not just on dialog open: the Modules & Tools tab
      // resolves each free-text key through the catalog to link governed rows
      // to their Module 360. Latched + best-effort, so this costs one request
      // per store lifetime and a failure just leaves the rows unlinked.
      void this.loadModuleCatalog();
      this.loadContacts();
      this.loadActivity();
      this.loadDocuments();
      this.loadSummary();
      this.loadHealth();
      this.loadHealthHistory();
      this.loadBilling();
      this.loadMeetings();
      this.loadWhiteboard();
      this.loadDevOpsLinks();
      this.loadInstances();
      this.loadHappiness();
      this.loadRelationshipItems();
      this.loadEmailMessages();
    } catch (err) {
      console.error("Failed to fan out client detail loads:", err);
    }
  }

  /** The load behind each section — what {@link retrySection} re-runs. */
  private sectionLoader(key: ClientSectionKey): () => Promise<unknown> {
    switch (key) {
      case "modules":
        return () => this.loadModules();
      case "tools":
        return () => this.loadTools();
      case "contacts":
        return () => this.loadContacts();
      case "activity":
        return () => this.loadActivity();
      case "documents":
        return () => this.loadDocuments();
      case "summary":
        return () => this.loadSummary();
      case "health":
        return () => this.loadHealth();
      case "healthHistory":
        return () => this.loadHealthHistory();
      case "billing":
        return () => this.loadBilling();
      case "meetings":
        return () => this.loadMeetings();
      case "whiteboard":
        return () => this.loadWhiteboard();
      case "devopsLinks":
        return () => this.loadDevOpsLinks();
      case "instances":
        return () => this.loadInstances();
      case "happiness":
        return () => this.loadHappiness();
      case "relationship":
        return () => this.loadRelationshipItems();
      case "emailMessages":
        return () => this.loadEmailMessages();
    }
  }

  /** Re-run one section's load (a tab's Retry). Clears its error first. */
  async retrySection(key: ClientSectionKey): Promise<void> {
    this.sectionErrors[key] = null;
    await this.sectionLoader(key)();
  }

  /** Record a section's outcome: null on success, the reason on failure. */
  private setSectionError(key: ClientSectionKey, err: unknown, fallback?: string) {
    runInAction(() => {
      // A call the sign-out ended is "no error": the store was reset under it.
      this.sectionErrors[key] =
        err == null ? null : loadErrorOrNull(err, fallback ?? "This data could not be loaded.");
    });
  }

  async loadModules() {
    if (!this.clientId) return;
    this.modulesLoading = true;
    this.setSectionError("modules", null);
    try {
      const result = await findOrThrow(this.clientModulesService, {
        query: {
          clientId: this.clientId,
          $limit: 200,
          $sort: { displayName: 1 },
        },
      });
      runInAction(() => {
        this.modules = rowsOf<ClientModuleRecord>(result as unknown as FindResult<ClientModuleRecord>);
      });
      void this.loadModuleTaskCounts();
    } catch (err) {
      console.error("Failed to load client modules:", err);
      this.setSectionError("modules", err, "This client's modules could not be loaded.");
    } finally {
      runInAction(() => {
        this.modulesLoading = false;
      });
    }
  }

  /**
   * Roll up this client's tracked work per module for the Deliverables tab —
   * how much work (open/total) each module/tool/integration carries.
   *
   * Source of truth is `read-ai-assignments`: a meeting action item, once
   * distributed, is attributed to a module via `read_ai_assignments.moduleId` and
   * mirrored into an Azure DevOps work item. With `project_tasks` gone this is
   * the ONLY per-module work signal in the product, so the chip reads it here.
   *
   * `rejected` rows are discarded outright (they were never real work);
   * `completed` rows still count toward `total` but not toward `open`.
   * Best-effort: a failure just leaves the column empty.
   *
   * LIVE (#3820): re-read when a colleague's accept, reject, completion or
   * module change touches an assignment this read counted, or a meeting of
   * this client is analysed (new work distributed) — see
   * {@link handleClientWorkEvent} and {@link handleClientMeetingEvent}.
   */
  async loadModuleTaskCounts() {
    if (!this.clientId) return;
    const clientId = this.clientId;
    // The newest count wins: a live re-count can start while an earlier one is out.
    const seq = ++this.taskCountsSeq;
    const moduleIds = this.modules.map((m) => m.id);
    if (moduleIds.length === 0) {
      this.taskCountAssignmentIds = new Set();
      runInAction(() => {
        this.moduleTaskCounts = {};
      });
      return;
    }
    try {
      const result = await this.assignmentsService?.find({
        query: {
          clientId,
          moduleId: { $in: moduleIds },
          $limit: 1000,
        },
      });
      // Another client is open now, or a newer count is out: this answer is not the one to show.
      if (seq !== this.taskCountsSeq || this.clientId !== clientId) return;
      const assignments = rowsOf<{ id?: string | number | null; moduleId?: string | null; status?: string }>(
        result as unknown as FindResult<{ id?: string | number | null; moduleId?: string | null; status?: string }>
      );
      const counts: Record<string, { open: number; total: number }> = {};
      for (const item of assignments) {
        if (!item.moduleId) continue;
        if (item.status === "rejected") continue;
        const entry = (counts[item.moduleId] ??= { open: 0, total: 0 });
        entry.total += 1;
        if (item.status !== "completed") entry.open += 1;
      }
      // Every row the read returned, counted or not: one with no module (or
      // not one of these) is counted the moment an accept-with-edits or a
      // set-module moves it into one, and that notice names only the
      // assignment. They ARE in the answer: `read-ai-assignments` find filters
      // by `clientId` and ignores `moduleId` (verified 2026-11-01, Codex r1), so
      // the `$in` above narrows nothing — keep this set whole if it ever does.
      this.taskCountAssignmentIds = new Set(
        assignments.map((a) => a.id).filter((id): id is string | number => id !== undefined && id !== null).map(String)
      );
      runInAction(() => {
        this.moduleTaskCounts = counts;
      });
    } catch (err) {
      console.error("Failed to load module work counts:", err);
    }
  }

  async loadTools() {
    if (!this.clientId) return;
    this.toolsLoading = true;
    this.setSectionError("tools", null);
    try {
      const result = await findOrThrow(this.clientToolsService, {
        query: {
          clientId: this.clientId,
          $limit: 200,
          $sort: { name: 1 },
        },
      });
      runInAction(() => {
        this.tools = rowsOf<ClientToolRecord>(result as unknown as FindResult<ClientToolRecord>);
      });
    } catch (err) {
      console.error("Failed to load client tools:", err);
      this.setSectionError("tools", err, "This client's tools could not be loaded.");
    } finally {
      runInAction(() => {
        this.toolsLoading = false;
      });
    }
  }

  // ---- Modules CRUD ----

  /**
   * Load the org-wide module vocabulary for the dialog's moduleKey picker.
   * Best-effort and cached for the store's lifetime: a failure or an empty
   * catalog just leaves the picker free-text.
   */
  async loadModuleCatalog() {
    if (this.moduleCatalogLoaded) return;
    // Several places open the picker at once (the tab's two loaders, Add Link,
    // Edit Link), and `loaded` only latches on SUCCESS — so without this the
    // same read is dispatched several times over. The in-flight promise is
    // shared; the first caller does the work and the rest await its answer.
    if (this.moduleCatalogInFlight) return this.moduleCatalogInFlight;
    // Clear the previous failure NOW, not when the retry answers: the picker
    // tells the operator to reopen the dialog to retry, and leaving the old
    // message up while the retry is in flight says the retry already failed.
    this.moduleCatalogError = null;
    const run = this.runModuleCatalogLoad();
    this.moduleCatalogInFlight = run;
    try {
      await run;
    } finally {
      runInAction(() => {
        // Only our own handle: a sign-out reset() may have cleared it and a
        // newer load taken the slot.
        if (this.moduleCatalogInFlight === run) this.moduleCatalogInFlight = null;
      });
    }
  }

  private async runModuleCatalogLoad() {
    const ended = sessionMark(this.rootStore);
    try {
      const result = await this.moduleCatalogService?.find({
        query: {
          $limit: 200,
          $sort: { displayName: 1 },
        },
      });
      // The wrapper SWALLOWS a rejection (records `errorOnFind`, resolves
      // null). Turning that into an empty catalog and latching `loaded` would
      // leave the Module picker offering only "No module" for the store's
      // lifetime, with no failure shown — so a null is a failure here.
      // A sign-out in the meantime: nothing to show the next person, and above
      // all nothing to latch — `moduleCatalogLoaded` over an empty catalog would
      // leave the picker offering only "No module" for the store's lifetime.
      if (ended()) return;
      if (result == null) {
        throw swallowedFailure(
          this.moduleCatalogService,
          "errorOnFind",
          "The module catalog could not be loaded"
        );
      }
      runInAction(() => {
        this.moduleCatalogError = null;
        const rows = rowsOf<ModuleCatalogRecord>(result as unknown as FindResult<ModuleCatalogRecord>);
        // LIST != RESOLVE (Module-Coordinator docs/CATALOG.md §3). The catalog now
        // mirrors `core`, `libraries` and `payroll` too, so the Modules page can
        // show them — but they are NOT assignable to a client, and offering the
        // platform every client already runs as a client module is the exact
        // mistake that split exists to prevent. Filtered here rather than in the
        // query so a web build that lands before the API migration still works:
        // only an explicit false excludes, an absent field is assignable.
        this.moduleCatalog = rows
          .filter((m) => m.assignable !== false)
          .slice()
          .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER));
        // Latch only on success — a transient failure (offline blip) retries
        // on the next dialog open instead of stranding a free-text-only picker.
        this.moduleCatalogLoaded = true;
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.moduleCatalogError = errorMessage(err, "The module catalog could not be loaded");
      });
      console.error("Failed to load module catalog:", err);
    }
  }

  startCreateModule() {
    this.editingModuleId = null;
    this.moduleFormError = null;
    this.moduleForm = {
      moduleKey: "",
      displayName: "",
      status: "enabled",
      isDeployable: false,
      metadata: "",
    };
    void this.loadModuleCatalog();
  }

  startEditModule(module: ClientModuleRecord) {
    this.editingModuleId = module.id;
    this.moduleFormError = null;
    this.moduleForm = {
      moduleKey: module.moduleKey,
      displayName: module.displayName,
      status: module.status,
      isDeployable: module.isDeployable,
      metadata: module.metadata || "",
    };
    void this.loadModuleCatalog();
  }

  setModuleField(field: keyof ClientModuleFormData, value: string | boolean) {
    this.moduleForm = { ...this.moduleForm, [field]: value };
  }

  /**
   * Apply a catalog pick to the module form: sets the key and auto-fills
   * displayName/isDeployable from the catalog entry (both stay editable).
   */
  applyCatalogModule(entry: ModuleCatalogRecord) {
    this.moduleForm = {
      ...this.moduleForm,
      moduleKey: entry.moduleKey,
      displayName: entry.displayName,
      isDeployable: entry.isDeployable,
    };
  }

  async saveModule(): Promise<boolean> {
    if (!this.clientId || !this.moduleForm.moduleKey || !this.moduleForm.displayName) {
      return false;
    }
    this.moduleSaving = true;
    this.moduleFormError = null;
    // The reload swallows a sign-out: the form it would clear is the next person's.
    const ended = sessionMark(this.rootStore);
    try {
      const payload: Record<string, unknown> = {
        clientId: this.clientId,
        moduleKey: this.moduleForm.moduleKey,
        displayName: this.moduleForm.displayName,
        status: this.moduleForm.status || "enabled",
        isDeployable: !!this.moduleForm.isDeployable,
      };
      if (this.moduleForm.metadata) payload.metadata = this.moduleForm.metadata;

      // THROW_ON_ERROR: the ServiceStore otherwise RESOLVES a refused write
      // (null), and this save reported it as saved and closed the dialog.
      if (this.editingModuleId) {
        await this.clientModulesService?.patch(this.editingModuleId, payload, THROW_ON_ERROR);
      } else {
        await this.clientModulesService?.create(payload as never, THROW_ON_ERROR);
      }
      await this.loadModules();
      if (ended()) return false;
      runInAction(() => {
        this.editingModuleId = null;
        this.moduleForm = {};
      });
      return true;
    } catch (err) {
      console.error("Failed to save module:", err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.moduleFormError = errorMessage(err, "The module could not be saved.");
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.moduleSaving = false;
        });
      }
    }
  }

  // Row actions below pass THROW_ON_ERROR: the ServiceStore otherwise
  // RESOLVES a refused write, and these used to report nothing but a console
  // line — a refused delete looked like a delete that had not refreshed yet.

  async toggleModuleStatus(module: ClientModuleRecord) {
    const next = module.status === "enabled" ? "disabled" : "enabled";
    this.deliverableActionError = null;
    try {
      await this.clientModulesService?.patch(module.id, { status: next }, THROW_ON_ERROR);
      await this.loadModules();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.deliverableActionError = errorMessage(
            err,
            `The module could not be ${next === "enabled" ? "enabled" : "disabled"}.`
          );
        }
      });
      console.error("Failed to toggle module status:", err);
    }
  }

  async removeModule(id: string) {
    this.deliverableActionError = null;
    try {
      await this.clientModulesService?.remove(id, THROW_ON_ERROR);
      await this.loadModules();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.deliverableActionError = errorMessage(err, "The module could not be deleted.");
      });
      console.error("Failed to remove module:", err);
    }
  }

  clearDeliverableActionError() {
    this.deliverableActionError = null;
  }

  // ---- Tools CRUD ----

  startCreateTool() {
    this.editingToolId = null;
    this.toolFormError = null;
    this.toolForm = {
      toolKey: "",
      toolType: "internal",
      name: "",
      url: "",
      status: "active",
    };
  }

  startEditTool(tool: ClientToolRecord) {
    this.editingToolId = tool.id;
    this.toolFormError = null;
    this.toolForm = {
      toolKey: tool.toolKey,
      toolType: tool.toolType,
      name: tool.name,
      url: tool.url || "",
      status: tool.status,
    };
  }

  setToolField(field: keyof ClientToolFormData, value: string) {
    this.toolForm = { ...this.toolForm, [field]: value };
  }

  async saveTool(): Promise<boolean> {
    if (!this.clientId || !this.toolForm.toolKey || !this.toolForm.name) {
      return false;
    }
    this.toolSaving = true;
    this.toolFormError = null;
    // The reload swallows a sign-out: the form it would clear is the next person's.
    const ended = sessionMark(this.rootStore);
    try {
      const payload: Record<string, unknown> = {
        clientId: this.clientId,
        toolKey: this.toolForm.toolKey,
        toolType: this.toolForm.toolType || "internal",
        name: this.toolForm.name,
        status: this.toolForm.status || "active",
      };
      if (this.toolForm.url) payload.url = this.toolForm.url;

      if (this.editingToolId) {
        await this.clientToolsService?.patch(this.editingToolId, payload, THROW_ON_ERROR);
      } else {
        await this.clientToolsService?.create(payload as never, THROW_ON_ERROR);
      }
      await this.loadTools();
      if (ended()) return false;
      runInAction(() => {
        this.editingToolId = null;
        this.toolForm = {};
      });
      return true;
    } catch (err) {
      console.error("Failed to save tool:", err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.toolFormError = errorMessage(err, "The tool could not be saved.");
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.toolSaving = false;
        });
      }
    }
  }

  async removeTool(id: string) {
    this.deliverableActionError = null;
    try {
      await this.clientToolsService?.remove(id, THROW_ON_ERROR);
      await this.loadTools();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.deliverableActionError = errorMessage(err, "The tool could not be deleted.");
      });
      console.error("Failed to remove tool:", err);
    }
  }

  // ---- DevOps links (DevOpsLinksTab) --------------------------------------

  get devopsLinkCount(): number {
    return this.devopsLinks.length;
  }

  /** Load this client's persisted Azure DevOps links (scoped by clientId). */
  async loadDevOpsLinks() {
    if (!this.clientId) return;
    this.devopsLinksLoading = true;
    this.setSectionError("devopsLinks", null);
    try {
      const result = await findOrThrow(this.devopsLinksService, {
        query: {
          clientId: this.clientId,
          $limit: 200,
          $sort: { createdAt: -1 },
        },
      });
      runInAction(() => {
        // devops-links is paginated; rowsOf handles either shape safely.
        this.devopsLinks = rowsOf<DevOpsLink>(result as unknown as FindResult<DevOpsLink>);
      });
      // Enrich the static link rows with live Azure status (per-link, silent).
      void this.loadDevOpsLinkStatuses();
    } catch (err) {
      console.error("Failed to load DevOps links:", err);
      this.setSectionError("devopsLinks", err, "This client's DevOps links could not be loaded.");
    } finally {
      runInAction(() => {
        this.devopsLinksLoading = false;
      });
    }
  }

  /** Lazily load the list of connections for the create-link picker. */
  async loadDevOpsConnections() {
    if (this.devopsConnections.length > 0) return;
    try {
      const result = await this.devopsConnectionsService?.find({
        query: {
          $limit: 200,
          $sort: { name: 1 },
        },
      });
      runInAction(() => {
        // devops-connections is a non-paginated proxy → bare array.
        this.devopsConnections = rowsOf<DevOpsConnection>(result as unknown as FindResult<DevOpsConnection>);
      });
    } catch (err) {
      console.error("Failed to load DevOps connections:", err);
    }
  }

  connectionName(id?: string | null): string {
    if (!id) return "";
    return this.devopsConnections.find((c) => c.id === id)?.name || "";
  }

  /**
   * Module-select options for the link dialogs: the assignable, non-deleted
   * catalog rows (`moduleCatalog` is already assignable-filtered on load), each
   * as its governed slug + display name. Empty until the catalog has loaded —
   * the select then offers "No module" only, which is honest: nothing to pick.
   */
  get devopsLinkModuleOptions(): DevOpsLinkModuleOption[] {
    return this.moduleCatalog
      .filter((m) => !m.deletedAt)
      .map((m) => ({
        slug: governedSlugOf(m),
        displayName: m.displayName,
        moduleKey: m.moduleKey,
      }));
  }

  /**
   * The display name of a link's governed slug — or the slug itself when the
   * catalog does not list it (a non-assignable row set elsewhere, or a catalog
   * that has not loaded). Never blank for a set slug; "" for none.
   */
  devopsLinkModuleName(slug: string | null | undefined): string {
    if (!slug) return "";
    const hit = this.moduleCatalog.find((m) => governedSlugOf(m) === slug);
    return hit?.displayName || slug;
  }

  // ---- Create-link form ----

  startCreateDevOpsLink() {
    this.editingDevOpsLinkId = null;
    this.devopsLinkError = null;
    this.devopsLinkForm = {
      linkType: "project",
      clientId: this.clientId,
      connectionId: this.devopsConnections[0]?.id,
      devopsProjectId: "",
      devopsProjectName: "",
      devopsEntityId: "",
      devopsEntityName: "",
      governedSlug: null,
      subscribeEvents: [],
    };
    this.devopsLinkSubscriptionErrors = [];
    this.devopsProjects = [];
    this.devopsRepos = [];
    this.devopsPipelines = [];
    // Pull projects for the pre-selected connection if any.
    if (this.devopsLinkForm.connectionId) {
      this.loadDevOpsProjectsForForm(this.devopsLinkForm.connectionId);
    }
  }

  /** Connect-wizard: toggle an event type in the "Subscribe to events" group. */
  toggleDevOpsLinkSubscription(eventType: string) {
    const current = this.devopsLinkForm.subscribeEvents || [];
    this.devopsLinkForm = {
      ...this.devopsLinkForm,
      subscribeEvents: current.includes(eventType)
        ? current.filter((t) => t !== eventType)
        : [...current, eventType],
    };
  }

  /** Dismiss the per-subscription failure warning shown in the tab. */
  clearDevOpsLinkSubscriptionErrors() {
    this.devopsLinkSubscriptionErrors = [];
  }

  setDevOpsLinkField(
    field: keyof ClientDevOpsLinkForm,
    value: string | null
  ) {
    this.devopsLinkForm = {
      ...this.devopsLinkForm,
      [field]: value,
    } as ClientDevOpsLinkForm;
  }

  /** Module select (create + edit): an empty choice clears the attribution. */
  setDevOpsLinkModule(slug: string | null) {
    this.devopsLinkForm = {
      ...this.devopsLinkForm,
      governedSlug: slug || null,
    };
  }

  /**
   * Edit dialog: the row's identity (connection, project, entity) is fixed —
   * the API's patch does not move a link between projects — and the client is
   * this one, so the only field this surface changes is the module.
   */
  startEditDevOpsLink(link: DevOpsLink) {
    this.editingDevOpsLinkId = link.id;
    this.devopsLinkError = null;
    this.devopsLinkSubscriptionErrors = [];
    this.devopsLinkForm = {
      linkType: link.linkType,
      clientId: link.clientId ?? this.clientId,
      connectionId: link.connectionId,
      devopsProjectId: link.devopsProjectId,
      devopsProjectName: link.devopsProjectName ?? "",
      devopsEntityId: link.devopsEntityId ?? "",
      devopsEntityName: link.devopsEntityName ?? "",
      governedSlug: link.governedSlug ?? null,
    };
  }

  /** Close the edit dialog without writing. */
  cancelDevOpsLinkEdit() {
    this.editingDevOpsLinkId = null;
    this.devopsLinkForm = {};
    this.devopsLinkError = null;
  }

  /** Connection change → reset selection + reload projects. */
  async onDevOpsConnectionChange(connectionId: string) {
    runInAction(() => {
      this.devopsLinkForm = {
        ...this.devopsLinkForm,
        connectionId,
        devopsProjectId: "",
        devopsProjectName: "",
        devopsEntityId: "",
        devopsEntityName: "",
      };
      this.devopsProjects = [];
      this.devopsRepos = [];
      this.devopsPipelines = [];
    });
    await this.loadDevOpsProjectsForForm(connectionId);
  }

  /** Link-type change → clear the entity selection and reload entities. */
  async onDevOpsLinkTypeChange(linkType: DevOpsLinkType) {
    runInAction(() => {
      this.devopsLinkForm = {
        ...this.devopsLinkForm,
        linkType,
        devopsEntityId: "",
        devopsEntityName: "",
      };
      this.devopsRepos = [];
      this.devopsPipelines = [];
    });
    if (this.devopsLinkForm.devopsProjectId) {
      await this.loadDevOpsEntitiesForForm();
    }
  }

  /** Project change → record id+name, then reload repos/pipelines if needed. */
  async onDevOpsProjectChange(projectId: string) {
    const project = this.devopsProjects.find((p) => p.id === projectId);
    runInAction(() => {
      this.devopsLinkForm = {
        ...this.devopsLinkForm,
        devopsProjectId: projectId,
        devopsProjectName: project?.name || "",
        devopsEntityId: "",
        devopsEntityName: "",
      };
      this.devopsRepos = [];
      this.devopsPipelines = [];
    });
    if (this.devopsLinkForm.linkType !== "project") {
      await this.loadDevOpsEntitiesForForm();
    }
  }

  onDevOpsEntityChange(entityId: string) {
    const isRepo = this.devopsLinkForm.linkType === "repo";
    const name = isRepo
      ? this.devopsRepos.find((r) => r.id === entityId)?.name
      : this.devopsPipelines.find((p) => String(p.id) === entityId)?.name;
    this.devopsLinkForm = {
      ...this.devopsLinkForm,
      devopsEntityId: entityId,
      devopsEntityName: name || "",
    };
  }

  /**
   * The wizard's project picker. The registered ServiceStore SWALLOWS a
   * rejected find — it records `errorOnFind` and resolves null — so an Azure
   * 503 arrives as `rowsOf(null)` === `[]`, the picker says "No projects",
   * and the operator concludes the org is empty. Treat the null as the
   * failure it is, name it in `devopsLinkError` (the alert the wizard already
   * renders), and leave whatever projects were loaded standing.
   */
  private async loadDevOpsProjectsForForm(connectionId: string) {
    this.devopsProjectsLoading = true;
    // Clear the previous failure as the retry STARTS.
    this.devopsLinkError = null;
    const ended = sessionMark(this.rootStore);
    try {
      const result = await this.devopsProjectsService?.find({
        query: {
          connectionId,
          $limit: 500,
        },
      });
      if (ended()) return;
      if (result == null) {
        throw swallowedFailure(
          this.devopsProjectsService,
          "errorOnFind",
          "Failed to load DevOps projects"
        );
      }
      runInAction(() => {
        this.devopsProjects = rowsOf<DevOpsProject>(result as unknown as FindResult<DevOpsProject>);
      });
    } catch (err) {
      console.error("Failed to load DevOps projects:", err);
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.devopsLinkError = errorMessage(err, "Failed to load DevOps projects");
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.devopsProjectsLoading = false;
        });
      }
    }
  }

  private async loadDevOpsEntitiesForForm() {
    const { connectionId, devopsProjectId, linkType } = this.devopsLinkForm;
    if (!connectionId || !devopsProjectId) return;
    this.devopsEntitiesLoading = true;
    this.devopsLinkError = null;
    const ended = sessionMark(this.rootStore);
    try {
      if (linkType === "repo") {
        const result = await this.devopsReposService?.find({
          query: {
            connectionId,
            projectId: devopsProjectId,
            $limit: 500,
          },
        });
        if (ended()) return;
        // A swallowed rejection, not a project with no repositories — the
        // picker would otherwise offer nothing and explain nothing.
        if (result == null) {
          throw swallowedFailure(
            this.devopsReposService,
            "errorOnFind",
            "Failed to load DevOps repositories"
          );
        }
        runInAction(() => {
          this.devopsRepos = rowsOf<DevOpsRepo>(result as unknown as FindResult<DevOpsRepo>);
        });
      } else if (linkType === "pipeline") {
        const result = await this.devopsPipelinesService?.find({
          query: {
            connectionId,
            projectId: devopsProjectId,
            $limit: 500,
          },
        });
        if (ended()) return;
        if (result == null) {
          throw swallowedFailure(
            this.devopsPipelinesService,
            "errorOnFind",
            "Failed to load DevOps pipelines"
          );
        }
        runInAction(() => {
          this.devopsPipelines = rowsOf<DevOpsPipelineApiRecord>(result as unknown as FindResult<DevOpsPipelineApiRecord>).map(normalizePipeline);
        });
      }
    } catch (err) {
      console.error("Failed to load DevOps entities:", err);
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.devopsLinkError = errorMessage(err, "Failed to load the project's entities");
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.devopsEntitiesLoading = false;
        });
      }
    }
  }

  get isDevOpsLinkValid(): boolean {
    const f = this.devopsLinkForm;
    if (!f.connectionId || !f.devopsProjectId) return false;
    if (f.linkType === "project") return true;
    // repo + pipeline links require a chosen entity.
    return !!f.devopsEntityId;
  }

  async saveDevOpsLink(): Promise<boolean> {
    if (!this.clientId || !this.isDevOpsLinkValid) return false;
    this.devopsLinkSaving = true;
    this.devopsLinkError = null;
    // The wrapper fallback below turns a swallowed SessionEnded into a plain
    // Error, so the session is asked directly as well as by the error.
    const ended = sessionMark(this.rootStore);
    try {
      const f = this.devopsLinkForm;
      const payload: Record<string, unknown> = {
        linkType: f.linkType || "project",
        clientId: this.clientId,
        connectionId: f.connectionId,
        devopsProjectId: f.devopsProjectId,
      };
      if (f.devopsProjectName) payload.devopsProjectName = f.devopsProjectName;
      if (f.linkType !== "project") {
        payload.devopsEntityId = f.devopsEntityId;
        if (f.devopsEntityName) payload.devopsEntityName = f.devopsEntityName;
      }
      if (f.governedSlug) payload.governedSlug = f.governedSlug;

      // RAW client first: the wrapper swallows a refusal (a duplicate link is
      // a Conflict; a repo/pipeline link with no entity id is refused) and
      // would let the wizard go on to register webhooks for a link that was
      // never saved. A null result IS that swallowed refusal.
      const raw = this.rawService("devops-links");
      const created = raw
        ? await raw.create(payload)
        : await this.devopsLinksService?.create(payload as never);
      if (created == null) {
        throw swallowedFailure(
          this.devopsLinksService,
          "errorOnCreate",
          "The server did not confirm the link"
        );
      }

      // Connect-wizard: register the checked service-hook subscriptions AFTER
      // the link saves. Each failure is collected and surfaced as a warning —
      // the link itself is NEVER rolled back (it is independently useful).
      await this.createDevOpsLinkSubscriptions(f);
      if (ended()) return false;

      await this.loadDevOpsLinks();
      if (ended()) return false;
      runInAction(() => {
        this.devopsLinkForm = {};
      });
      return true;
    } catch (err) {
      console.error("Failed to save DevOps link:", err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.devopsLinkError = errorMessage(err, "Failed to save the DevOps link");
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.devopsLinkSaving = false;
        });
      }
    }
  }

  /**
   * Edit-dialog save: `devops-links.patch(id, { governedSlug })` — the only
   * field this surface changes (the client is fixed to this one; an empty
   * choice clears the attribution with null). Same raw-first / wrapper-fallback
   * rule as the create, for the same reason: a null result is a swallowed
   * refusal, never a saved edit.
   */
  async saveDevOpsLinkEdit(): Promise<boolean> {
    const id = this.editingDevOpsLinkId;
    if (!id) return false;
    this.devopsLinkSaving = true;
    this.devopsLinkError = null;
    const ended = sessionMark(this.rootStore);
    try {
      const payload = { governedSlug: this.devopsLinkForm.governedSlug || null };
      const raw = this.rawService("devops-links");
      const patched = raw
        ? await raw.patch(id, payload)
        : await this.devopsLinksService?.patch(id, payload);
      if (ended()) return false;
      if (patched == null) {
        throw swallowedFailure(
          this.devopsLinksService,
          "errorOnPatch",
          "The server did not confirm the change"
        );
      }
      await this.loadDevOpsLinks();
      if (ended()) return false;
      runInAction(() => {
        this.editingDevOpsLinkId = null;
        this.devopsLinkForm = {};
      });
      return true;
    } catch (err) {
      console.error("Failed to update DevOps link:", err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.devopsLinkError = errorMessage(err, "Failed to update the DevOps link");
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.devopsLinkSaving = false;
        });
      }
    }
  }

  /**
   * Create one `devops-subscriptions` row per checked wizard event type,
   * scoped via `publisherInputs.projectId` to the linked Azure project (plus
   * `definitionId` for pipeline links so build hooks fire only for the linked
   * pipeline). Failures are PER SUBSCRIPTION: each is caught, recorded in
   * {@link devopsLinkSubscriptionErrors}, and never thrown — so a failed hook
   * registration cannot roll back (or even fail) the link save.
   */
  private async createDevOpsLinkSubscriptions(f: DevOpsLinkFormData) {
    const eventTypes = f.subscribeEvents || [];
    if (eventTypes.length === 0 || !f.connectionId || !f.devopsProjectId) {
      return;
    }

    const errors: string[] = [];
    // A sign-out mid-loop ends the wizard: no more hooks registered for the
    // person who left, and no warning list written over the emptied store.
    const ended = sessionMark(this.rootStore);
    // Sequential on purpose — subscription create hits Azure through the
    // connection's rate-limited REST client; three at most, so latency is fine.
    for (const eventType of eventTypes) {
      try {
        const publisherInputs: Record<string, string> = {
          projectId: f.devopsProjectId,
        };
        // Build hooks on a pipeline link are scoped down to that definition.
        if (
          eventType.startsWith("build.") &&
          f.linkType === "pipeline" &&
          f.devopsEntityId
        ) {
          publisherInputs.definitionId = f.devopsEntityId;
        }
        const payload: DevOpsSubscriptionCreatePayload = {
          connectionId: f.connectionId,
          eventType,
          publisherInputs,
        };
        // The project's name rides along so the row is not a GUID with no
        // name (the DevOps overview would list it as `name: null`).
        if (f.devopsProjectName) payload.projectName = f.devopsProjectName;
        const created = await this.devopsSubscriptionsService?.create(payload as never);
        if (ended()) return;
        // `devops-subscriptions` keeps throwOnError off: a refusal RESOLVES
        // null with the reason on errorOnCreate, and used to count as success.
        if (created == null) {
          throw swallowedFailure(
            this.devopsSubscriptionsService,
            "errorOnCreate",
            "The server did not confirm the subscription"
          );
        }
      } catch (err) {
        console.error(`Failed to subscribe to ${eventType}:`, err);
        if (isSessionEnded(err) || ended()) return;
        errors.push(
          `${eventType}: ${err instanceof Error ? err.message : "subscription failed"}`
        );
      }
    }

    runInAction(() => {
      this.devopsLinkSubscriptionErrors = errors;
    });
  }

  async removeDevOpsLink(id: string) {
    this.devopsLinkActionError = null;
    try {
      await this.devopsLinksService?.remove(id, THROW_ON_ERROR);
      await this.loadDevOpsLinks();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.devopsLinkActionError = errorMessage(err, "The DevOps link could not be removed.");
      });
      console.error("Failed to remove DevOps link:", err);
    }
  }

  clearDevOpsLinkActionError() {
    this.devopsLinkActionError = null;
  }

  // ---- DevOps link live status (DevOpsLinksTab enrichment) -----------------

  /**
   * Batch-fetch live Azure status for the loaded links. Pipeline links resolve
   * the single definition's latest build (`devops-pipelines` get); project
   * links aggregate the project's definitions (find). Repo links are static
   * (no run status). DEGRADES SILENTLY per link — a failed lookup simply
   * leaves that row without a chip.
   */
  async loadDevOpsLinkStatuses() {
    const links = this.devopsLinks;
    await Promise.all(
      links.map(async (link) => {
        try {
          if (link.linkType === "pipeline" && link.devopsEntityId) {
            const rec = (await this.devopsPipelinesService?.get(
              link.devopsEntityId,
              {
                query: {
                  connectionId: link.connectionId,
                  projectId: link.devopsProjectId,
                },
              }
            )) as unknown as DevOpsPipelineApiRecord | undefined;
            if (!rec) return;
            const pipeline = normalizePipeline(rec);
            runInAction(() => {
              this.devopsLinkStatuses = {
                ...this.devopsLinkStatuses,
                [link.id]: {
                  kind: "pipeline",
                  status: pipeline.latestStatus || "none",
                  webUrl: pipeline.webUrl,
                },
              };
            });
          } else if (link.linkType === "project") {
            const result = await this.devopsPipelinesService?.find({
              query: {
                connectionId: link.connectionId,
                projectId: link.devopsProjectId,
                $limit: 200,
              },
            });
            const pipelines = rowsOf<DevOpsPipelineApiRecord>(result as unknown as FindResult<DevOpsPipelineApiRecord>).map(normalizePipeline);
            const agg = {
              kind: "project" as const,
              total: pipelines.length,
              succeeded: pipelines.filter((p) => p.latestStatus === "succeeded")
                .length,
              failed: pipelines.filter((p) => p.latestStatus === "failed").length,
              inProgress: pipelines.filter((p) => p.latestStatus === "inProgress")
                .length,
            };
            runInAction(() => {
              this.devopsLinkStatuses = {
                ...this.devopsLinkStatuses,
                [link.id]: agg,
              };
            });
          }
          // repo links: static — nothing to fetch.
        } catch {
          // Silent per-link degradation by design — the row renders without
          // a live chip and the rest of the tab is unaffected.
        }
      })
    );
  }

  // ---- Contacts CRUD (ContactsTab) -----------------------------------------

  async loadContacts() {
    if (!this.clientId) return;
    this.contactsLoading = true;
    this.setSectionError("contacts", null);
    try {
      const result = await findOrThrow(this.clientContactsService, {
        query: {
          clientId: this.clientId,
          $limit: 200,
          $sort: { name: 1 },
        },
      });
      runInAction(() => {
        this.contacts = rowsOf<ClientContactRecord>(result as unknown as FindResult<ClientContactRecord>);
      });
    } catch (err) {
      console.error("Failed to load client contacts:", err);
      this.setSectionError("contacts", err, "This client's contacts could not be loaded.");
    } finally {
      runInAction(() => {
        this.contactsLoading = false;
      });
    }
  }

  startCreateContact() {
    this.editingContactId = null;
    this.contactFormError = null;
    this.contactForm = {
      name: "",
      title: "",
      email: "",
      phone: "",
      isPrimary: this.contacts.length === 0,
      notes: "",
    };
  }

  startEditContact(contact: ClientContactRecord) {
    this.editingContactId = contact.id;
    this.contactFormError = null;
    this.contactForm = {
      name: contact.name,
      title: contact.title || "",
      email: contact.email || "",
      phone: contact.phone || "",
      isPrimary: !!contact.isPrimary,
      notes: contact.notes || "",
    };
  }

  setContactField(field: keyof ClientContactFormData, value: string | boolean) {
    this.contactForm = { ...this.contactForm, [field]: value };
  }

  async saveContact(): Promise<boolean> {
    if (!this.clientId || !this.contactForm.name) return false;
    this.contactSaving = true;
    this.contactFormError = null;
    // The reload swallows a sign-out: the form it would clear is the next person's.
    const ended = sessionMark(this.rootStore);
    try {
      const payload: Record<string, unknown> = {
        clientId: this.clientId,
        name: this.contactForm.name,
        isPrimary: !!this.contactForm.isPrimary,
      };
      if (this.contactForm.title) payload.title = this.contactForm.title;
      if (this.contactForm.email) payload.email = this.contactForm.email;
      if (this.contactForm.phone) payload.phone = this.contactForm.phone;
      if (this.contactForm.notes) payload.notes = this.contactForm.notes;

      if (this.editingContactId) {
        await this.clientContactsService?.patch(this.editingContactId, payload, THROW_ON_ERROR);
      } else {
        await this.clientContactsService?.create(payload as never, THROW_ON_ERROR);
      }
      await this.loadContacts();
      if (ended()) return false;
      runInAction(() => {
        this.editingContactId = null;
        this.contactForm = {};
      });
      return true;
    } catch (err) {
      console.error("Failed to save contact:", err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.contactFormError = errorMessage(err, "The contact could not be saved.");
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.contactSaving = false;
        });
      }
    }
  }

  /**
   * Toggle the primary star. When promoting a contact, demote any other
   * primary row first so the client keeps a single primary contact.
   */
  async toggleContactPrimary(contact: ClientContactRecord) {
    this.contactActionError = null;
    try {
      const next = !contact.isPrimary;
      if (next) {
        const current = this.contacts.filter(
          (c) => c.isPrimary && c.id !== contact.id
        );
        for (const c of current) {
          await this.clientContactsService?.patch(c.id, { isPrimary: false }, THROW_ON_ERROR);
        }
      }
      await this.clientContactsService?.patch(contact.id, { isPrimary: next }, THROW_ON_ERROR);
      await this.loadContacts();
    } catch (err) {
      console.error("Failed to toggle primary contact:", err);
      // The sign-out ended it: no error, and no re-read in the next session.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.contactActionError = errorMessage(err, "The primary contact could not be changed.");
      });
      // A demotion may have landed before the refusal: show what is stored.
      await this.loadContacts();
    }
  }

  async removeContact(id: string) {
    this.contactActionError = null;
    try {
      await this.clientContactsService?.remove(id, THROW_ON_ERROR);
      await this.loadContacts();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.contactActionError = errorMessage(err, "The contact could not be deleted.");
      });
      console.error("Failed to remove contact:", err);
    }
  }

  clearContactActionError() {
    this.contactActionError = null;
  }

  // ---- Activity timeline (ActivityTab) --------------------------------------

  /** Rows per timeline page ("Show more" reveals another page). */
  static readonly ACTIVITY_PAGE_SIZE = 25;

  /**
   * The merged, newest-first timeline: communications + audit-log +
   * devops-events, each normalised into a render-ready item.
   */
  get activityItems(): ClientActivityItem[] {
    const items: ClientActivityItem[] = [];

    for (const c of this.communications) {
      items.push({
        key: `communication-${c.id}`,
        type: "communication",
        at: c.occurredAt || c.createdAt || "",
        title: c.subject || c.sourceType,
        detail: c.summary || undefined,
      });
    }
    for (const a of this.auditEntries) {
      items.push({
        key: `audit-${a.id}`,
        type: "audit",
        at: a.createdAt || "",
        title: `${a.action} ${a.entityType}`,
        detail: a.entityId ? `#${a.entityId}` : undefined,
      });
    }
    for (const e of this.devopsEvents) {
      let detail: string | undefined;
      if (e.summary && typeof e.summary === "object") {
        const s = e.summary as Record<string, unknown>;
        detail =
          (typeof s.buildNumber === "string" && s.buildNumber) ||
          (typeof s.title === "string" && s.title) ||
          undefined;
      } else if (typeof e.summary === "string") {
        // Persisted as a JSON string — pull a short label without throwing.
        try {
          const s = JSON.parse(e.summary) as Record<string, unknown>;
          detail =
            (typeof s.buildNumber === "string" && s.buildNumber) ||
            (typeof s.title === "string" && s.title) ||
            undefined;
        } catch {
          detail = undefined;
        }
      }
      items.push({
        key: `devops-${e.id}`,
        type: "devops",
        at: e.occurredAt || e.createdAt || "",
        title: e.title || e.type,
        detail,
      });
    }

    return items.sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
    );
  }

  /** The slice of the timeline currently shown (paged). */
  get pagedActivityItems(): ClientActivityItem[] {
    return this.activityItems.slice(
      0,
      this.activityPage * ClientDetailStore.ACTIVITY_PAGE_SIZE
    );
  }

  get hasMoreActivity(): boolean {
    return this.pagedActivityItems.length < this.activityItems.length;
  }

  /**
   * True when ANY of the three timeline sources came back at its page cap.
   *
   * The merged timeline is paged locally with "Show more", so once the button
   * disappears the list reads as "that is the whole history" — which is false
   * as soon as a source is capped. The tab renders a caption in that case.
   */
  get activityCapped(): boolean {
    return (
      this.communications.length >= ACTIVITY_SOURCE_LIMIT ||
      this.auditEntries.length >= ACTIVITY_SOURCE_LIMIT ||
      this.devopsEvents.length >= ACTIVITY_SOURCE_LIMIT
    );
  }

  /**
   * Whether the DevOps half of this timeline needs its attribution caveat
   * ({@link DEVOPS_ATTRIBUTION_NOTE}) — true once the client has any
   * `devops-links` row, because only then can a project of its have activity
   * HQ declined to file under it.
   *
   * NOT a claim that anything was actually left unattributed. This page reads
   * `devops-events` by `{ clientId }`, so the unattributed rows are exactly
   * the rows it cannot see; proving one exists would take a second server read
   * (every event on this client's projects, then a comparison) and even that
   * would miss the rows whose project columns are name-only. Rather than
   * compute a "nothing was dropped" that is really "we did not look", the tab
   * states the reading — the honest half of the Overview's
   * `unattributed-activity`, which the API computes and can therefore assert.
   *
   * Deliberately independent of whether the timeline is empty: a populated
   * list is just as partial, and hiding the caveat there would make the claim
   * only when it is hardest to notice.
   */
  get devopsAttributionCaveat(): boolean {
    return this.devopsLinks.length > 0;
  }

  showMoreActivity() {
    this.activityPage += 1;
  }

  /**
   * Load the three timeline sources in parallel. Each source degrades
   * independently — a 403/missing service does not blank the others — but
   * every failed source is named in `sectionErrors.activity`, so a partial
   * timeline is never presented as the whole history.
   */
  async loadActivity() {
    if (!this.clientId) return;
    this.activityLoading = true;
    this.activityPage = 1;
    this.setSectionError("activity", null);
    const clientId = this.clientId;
    // Each source still degrades independently (its slice stays as it was),
    // but a failure is RECORDED so the timeline never reads as complete.
    const failures: string[] = [];
    // A source can fail on its own just before a sign-out ends its siblings;
    // that failure is the previous person's, and the store was reset under it.
    const ended = sessionMark(this.rootStore);
    const source = <T>(
      label: string,
      service: ServiceStoreType | null,
      query: Record<string, unknown>,
      apply: (rows: T[]) => void
    ) =>
      findOrThrow(service, { query })
        .then((result) => {
          if (!ended()) runInAction(() => apply(rowsOf<T>(result as unknown as FindResult<T>)));
        })
        .catch((err) => {
          console.error(`Failed to load ${label}:`, err);
          if (!isSessionEnded(err)) failures.push(`${label}: ${loadErrorMessage(err)}`);
        });
    await Promise.all([
      source<CommunicationRecord>(
        "communications",
        this.communicationsService,
        { clientId, $limit: ACTIVITY_SOURCE_LIMIT, $sort: { occurredAt: -1 } },
        (rows) => {
          this.communications = rows;
        }
      ),
      source<AuditLogRecord>(
        "audit log",
        this.auditLogService,
        { clientId, $limit: ACTIVITY_SOURCE_LIMIT, $sort: { createdAt: -1 } },
        (rows) => {
          this.auditEntries = rows;
        }
      ),
      source<DevOpsEventRecord>(
        "DevOps events",
        this.devopsEventsService,
        { clientId, $limit: ACTIVITY_SOURCE_LIMIT },
        (rows) => {
          this.devopsEvents = rows;
        }
      ),
    ]);
    if (ended()) return;
    runInAction(() => {
      this.activityLoading = false;
      this.sectionErrors.activity = failures.length
        ? `Part of this client's activity could not be loaded — ${failures.join("; ")}`
        : null;
    });
  }


  setNoteSubject(value: string) {
    this.noteSubject = value;
  }

  setNoteSummary(value: string) {
    this.noteSummary = value;
  }

  get isNoteValid(): boolean {
    return this.noteSummary.trim().length > 0;
  }

  /** Quick-add note → a `communications` row with sourceType='note'. */
  async addNote(): Promise<boolean> {
    if (!this.clientId || !this.isNoteValid) return false;
    this.noteSaving = true;
    // The wrapper swallows a SessionEnded (resolves null), so the session is
    // asked directly: no reload started for the next person.
    const ended = sessionMark(this.rootStore);
    try {
      await this.communicationsService?.create({
        clientId: this.clientId,
        sourceType: "note",
        subject: this.noteSubject.trim() || null,
        summary: this.noteSummary.trim(),
        occurredAt: new Date().toISOString(),
      } as never);
      if (ended()) return false;
      runInAction(() => {
        this.noteSubject = "";
        this.noteSummary = "";
      });
      await this.loadActivity();
      return true;
    } catch (err) {
      console.error("Failed to add note:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.noteSaving = false;
        });
      }
    }
  }

  // ---- Documents (DocumentsTab) ---------------------------------------------

  async loadDocuments() {
    if (!this.clientId) return;
    this.documentsLoading = true;
    this.setSectionError("documents", null);
    try {
      // $sort on createdAt — uploadedAt is not in documentQueryProperties.
      const result = await findOrThrow(this.documentsService, {
        query: {
          entityType: "client",
          entityId: this.clientId,
          $limit: CLIENT_DOCUMENTS_LIMIT,
          $sort: { createdAt: -1 },
        },
      });
      runInAction(() => {
        this.documents = rowsOf<ClientDocumentRecord>(result as unknown as FindResult<ClientDocumentRecord>);
      });
    } catch (err) {
      console.error("Failed to load client documents:", err);
      this.setSectionError("documents", err, "This client's documents could not be loaded.");
    } finally {
      runInAction(() => {
        this.documentsLoading = false;
      });
    }
  }

  clearDocumentError() {
    this.documentError = null;
  }

  /**
   * Upload a file for this client via the documents service's RAW-BODY
   * endpoint: POST /documents/upload?entityType=client&entityId=<id>&category=…
   * with the File itself as the body (NOT FormData — the API's koa middleware
   * reads the raw stream; metadata travels in query params + X-File-Name).
   * Text files are sent as application/octet-stream so the app-level body
   * parser does not consume the stream first.
   */
  async uploadDocument(file: File): Promise<boolean> {
    if (!this.clientId) return false;
    this.documentUploading = true;
    this.documentError = null;
    // A raw fetch the session guard never sees (shared/sessionDownload.ts).
    const ended = sessionMark(this.rootStore);
    try {
      const { env } = await import("../../../config/environment");
      // Before the token is read: after a sign-out it is the next person's.
      if (ended()) return false;
      const token = this.rootStore.authStore?.token ?? null;

      const query = new URLSearchParams({
        entityType: "client",
        entityId: this.clientId,
        category: "other",
      });
      const contentType =
        file.type && !file.type.startsWith("text/")
          ? file.type
          : "application/octet-stream";
      const headers: Record<string, string> = {
        "Content-Type": contentType,
        "X-File-Name": encodeURIComponent(file.name),
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${env.apiUrl}/documents/upload?${query}`, {
        method: "POST",
        headers,
        body: file,
      });
      if (ended()) return false;
      if (!response.ok) {
        const text = await response.text();
        let message = `Upload failed (${response.status})`;
        try {
          const parsed = JSON.parse(text) as { message?: unknown };
          if (typeof parsed.message === "string" && parsed.message) {
            message = parsed.message;
          }
        } catch {
          if (text) message = text;
        }
        throw new Error(message);
      }
      await this.loadDocuments();
      return !ended();
    } catch (err) {
      console.error("Failed to upload document:", err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.documentError =
          err instanceof Error ? err.message : "Failed to upload document";
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.documentUploading = false;
        });
      }
    }
  }

  /**
   * Download a document's bytes via the authenticated stream endpoint
   * (GET /documents/:id/content with the Bearer header — the external
   * resolver strips storageUrl, and a plain <a href> would 401). The blob
   * is handed to the browser through a transient object URL.
   */
  async downloadDocument(doc: ClientDocumentRecord): Promise<boolean> {
    return this.downloadStoredDocument(
      doc.id,
      doc.originalFileName || doc.fileName || "download",
      "documentError",
      "Failed to download document"
    );
  }

  /**
   * GET /documents/:id/content with the Bearer header and save the bytes —
   * never after a sign-out: a raw fetch the session guard cannot see, so the
   * session is asked after every await (shared/sessionDownload.ts), and the
   * previous person's document is not saved to disk in the next one's session.
   */
  private async downloadStoredDocument(
    documentId: string,
    fileName: string,
    errorField: "documentError" | "uploadError",
    fallback: string
  ): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    try {
      const { env } = await import("../../../config/environment");
      // Before the token is read: after a sign-out it is the next person's.
      if (ended()) return false;
      const token = this.rootStore.authStore?.token ?? null;
      const response = await fetch(`${env.apiUrl}/documents/${encodeURIComponent(documentId)}/content`, {
        headers: bearerHeaders(token),
      });
      if (ended()) return false;
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const blob = await response.blob();
      return saveBlobInSession(ended, blob, fileName);
    } catch (err) {
      console.error(`${fallback}:`, err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this[errorField] = err instanceof Error ? err.message : fallback;
      });
      return false;
    }
  }

  async removeDocument(id: string) {
    this.documentError = null;
    try {
      await this.documentsService?.remove(id, THROW_ON_ERROR);
      await this.loadDocuments();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.documentError = errorMessage(err, "The document could not be deleted.");
      });
      console.error("Failed to remove document:", err);
    }
  }

  // ---- Rollups + health (Overview / header) ----------------------------------

  async loadSummary() {
    if (!this.clientId) return;
    this.setSectionError("summary", null);
    try {
      // NOTE: the query schema is additionalProperties:false with ONLY
      // clientId — no Feathers pagination params ($limit would 400).
      const result = await findOrThrow(this.clientSummaryService, {
        query: { clientId: this.clientId },
      });
      const rows = rowsOf<ClientSummaryRecord>(result as unknown as FindResult<ClientSummaryRecord>);
      runInAction(() => {
        this.summary = rows[0] || null;
      });
    } catch (err) {
      // Rollups are an enrichment — degrade silently to the local counts.
      console.error("Failed to load client summary:", err);
      this.setSectionError("summary", err, "The client summary could not be loaded.");
    }
  }

  async loadHealth() {
    if (!this.clientId) return;
    this.setSectionError("health", null);
    try {
      // Same contract as client-summary: clientId is the ONLY allowed param.
      const result = await findOrThrow(this.clientHealthService, {
        query: { clientId: this.clientId },
      });
      const rows = rowsOf<ClientHealthRecord>(result as unknown as FindResult<ClientHealthRecord>);
      runInAction(() => {
        this.health = rows[0] || null;
      });
    } catch (err) {
      // Health is an enrichment — no chip renders when unavailable.
      console.error("Failed to load client health:", err);
      this.setSectionError("health", err, "The client's health could not be loaded.");
    }
  }

  /**
   * Load the observed health verdicts behind the overview sparkline.
   *
   * The service is append-only and paginated, so we take the most recent N
   * DESC and reverse into chronological order for plotting. Enrichment only:
   * a failure (or an instance whose history table predates the client) simply
   * leaves the series empty and the sparkline unrendered.
   */
  async loadHealthHistory() {
    if (!this.clientId) return;
    this.setSectionError("healthHistory", null);
    try {
      const result = await findOrThrow(this.clientHealthHistoryService, {
        query: {
          clientId: this.clientId,
          $limit: HEALTH_HISTORY_LIMIT,
          $sort: { createdAt: -1 },
        },
      });
      const rows = rowsOf<ClientHealthHistoryRecord>(
        result as unknown as FindResult<ClientHealthHistoryRecord>
      );
      runInAction(() => {
        // Reverse the newest-first page into oldest→newest for the line.
        this.healthHistory = rows.slice().reverse();
      });
    } catch (err) {
      console.error("Failed to load client health history:", err);
      this.setSectionError("healthHistory", err, "The client's health history could not be loaded.");
    }
  }

  /** The sparkline series: one 0-100 point per observed verdict, in order. */
  get healthSeries(): Array<{ score: number; health: string; at: string }> {
    return this.healthHistory.map((row) => ({
      score: healthTierScore(row.health),
      health: String(row.health),
      at: row.createdAt,
    }));
  }

  // ---- Billing (BillingTab) -------------------------------------------------

  /**
   * Load this client's invoices — but only after the API confirms billing is
   * configured on this instance.
   *
   * Billing is optional and the same web image ships everywhere, so the answer
   * has to come from the running API (`billing-capabilities`, cached by the
   * billing module's capability store — see views/billing/billingGate.ts).
   * FAILS CLOSED: no capability store, or a failed report, means no tab.
   */
  async loadBilling() {
    if (!this.clientId) return;
    // The capability store answers a load the sign-out ended with its last
    // known report rather than throwing, so the session is asked here: a
    // `billingEnabled` written after the purge reset it to null is a verdict
    // nobody asked for, and an invoice find started now runs in the next session.
    const ended = sessionMark(this.rootStore);
    this.setSectionError("billing", null);
    let enabled = false;
    try {
      const capabilities = await this.billingCapabilities?.load();
      enabled = capabilities?.enabled === true;
    } catch (err) {
      console.error("Failed to read billing capabilities:", err);
      enabled = false;
    }
    if (ended()) return;
    runInAction(() => {
      this.billingEnabled = enabled;
      if (!enabled) this.invoices = [];
    });
    if (!enabled) return;

    runInAction(() => {
      this.invoicesLoading = true;
    });
    try {
      const result = await findOrThrow(this.invoicesService, {
        query: {
          clientId: this.clientId,
          $limit: CLIENT_INVOICES_LIMIT,
          $sort: { issueDate: -1 },
        },
      });
      runInAction(() => {
        this.invoices = rowsOf<ClientInvoiceRecord>(
          result as unknown as FindResult<ClientInvoiceRecord>
        );
      });
    } catch (err) {
      console.error("Failed to load client invoices:", err);
      this.setSectionError("billing", err, "This client's invoices could not be loaded.");
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.invoicesLoading = false;
        });
      }
    }
  }

  /** Invoices newest-first (the tab's row order). */
  get sortedInvoices(): ClientInvoiceRecord[] {
    return this.invoices
      .slice()
      .sort((a, b) =>
        String(b.issueDate ?? "").localeCompare(String(a.issueDate ?? ""))
      );
  }

  /**
   * True when the invoice load hit its page cap — older invoices exist but are
   * not LISTED. The tab's money figures no longer come from these rows: they
   * are the API's receivables aging (`ar-aging`, ClientReceivablesStore) over
   * every invoice, summed exactly. (The tab used to add up these capped rows as
   * floats, and could only caption the understatement.)
   */
  get invoicesCapped(): boolean {
    return this.invoices.length >= CLIENT_INVOICES_LIMIT;
  }

  // ---- Meetings (MeetingsTab) ----------------------------------------------

  /**
   * Load this client's ingested meetings. NOTE the service contract
   * (read-ai-meetings.class.ts): find returns a BARE ARRAY, always ordered
   * createdAt DESC, and honours only `clientId` / `processed` / `$limit` —
   * `$sort` is ignored, so the display order is applied here.
   */
  async loadMeetings() {
    if (!this.clientId) return;
    this.meetingsLoading = true;
    this.setSectionError("meetings", null);
    try {
      const result = await findOrThrow(this.readAiMeetingsService, {
        query: { clientId: this.clientId, $limit: CLIENT_MEETINGS_LIMIT },
      });
      runInAction(() => {
        this.meetings = rowsOf<ClientMeetingRecord>(
          result as unknown as FindResult<ClientMeetingRecord>
        );
      });
    } catch (err) {
      console.error("Failed to load client meetings:", err);
      this.setSectionError("meetings", err, "This client's meetings could not be loaded.");
    } finally {
      runInAction(() => {
        this.meetingsLoading = false;
      });
    }
  }

  // ---- Whiteboard (WhiteboardTab) ------------------------------------------

  /**
   * Load this client's whiteboard lanes and their cells for the recent window
   * (two weeks back through three ahead). Both finds are clientId/rowId-scoped
   * so the tab never renders another client's lanes.
   */
  async loadWhiteboard() {
    if (!this.clientId) return;
    this.whiteboardLoading = true;
    this.setSectionError("whiteboard", null);
    try {
      const rowsResult = await findOrThrow(this.whiteboardRowsService, {
        query: { clientId: this.clientId, $limit: 100, $sort: { sortOrder: 1 } },
      });
      const rows = rowsOf<ClientWhiteboardRow>(
        rowsResult as unknown as FindResult<ClientWhiteboardRow>
      ).filter((r) => !r.deletedAt);
      let cells: ClientWhiteboardCell[] = [];
      if (rows.length > 0) {
        const week = currentWeekStart();
        const cellsResult = await findOrThrow(this.whiteboardCellsService, {
          query: {
            rowId: { $in: rows.map((r) => r.id) },
            weekOf: { $gte: addWeeks(week, -2), $lte: addWeeks(week, 3) },
            $limit: 500,
          },
        });
        cells = rowsOf<ClientWhiteboardCell>(
          cellsResult as unknown as FindResult<ClientWhiteboardCell>
        ).filter((c) => !c.deletedAt);
      }
      runInAction(() => {
        this.whiteboardRows = rows;
        this.whiteboardCells = cells;
      });
      // A Sales Queue lane's chip is labelled from the board's deal-status
      // taxonomy (#3080), which this tab reads through the board's own store.
      // Tolerant: until it loads, a lane shows its stored status.
      if (rows.some((r) => r.board === "sales" && r.status)) {
        void (this.rootStore.whiteboardStore as { loadStatuses?: () => Promise<void> } | undefined)?.loadStatuses?.();
      }
    } catch (err) {
      console.error("Failed to load client whiteboard:", err);
      this.setSectionError("whiteboard", err, "This client's whiteboard could not be loaded.");
    } finally {
      runInAction(() => {
        this.whiteboardLoading = false;
      });
    }
  }

  /** Lanes sorted title-then-sortOrder (mirrors the board's ordering). */
  get whiteboardLanes(): ClientWhiteboardRow[] {
    return this.whiteboardRows
      .slice()
      .sort(
        (a, b) =>
          (a.title || "").localeCompare(b.title || "") ||
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      );
  }

  /** A lane's cell for one week key, if any. */
  whiteboardCellFor(rowId: string, weekOf: string): ClientWhiteboardCell | null {
    return (
      this.whiteboardCells.find(
        (c) => c.rowId === rowId && weekStartOf(c.weekOf) === weekOf
      ) ?? null
    );
  }

  /** Meetings newest-first, falling back to createdAt when unscheduled. */
  get sortedMeetings(): ClientMeetingRecord[] {
    const when = (m: ClientMeetingRecord) =>
      new Date(m.startTime || m.createdAt || 0).getTime();
    return this.meetings.slice().sort((a, b) => when(b) - when(a));
  }

  /** True when the load hit the page cap — older meetings exist but are hidden. */
  get meetingsCapped(): boolean {
    return this.meetings.length >= CLIENT_MEETINGS_LIMIT;
  }

  /**
   * Read a tolerant numeric rollup off the summary record. Only `instances`
   * remains — `client-summary` no longer returns project/task counts.
   */
  summaryCount(kind: "instances"): number | null {
    const s = this.summary;
    if (!s) return null;
    if (typeof s.instanceCount === "number") return s.instanceCount;
    const nested = s[kind];
    if (typeof nested === "number") return nested;
    if (nested && typeof nested === "object" && typeof nested.total === "number") {
      return nested.total;
    }
    return null;
  }

  // ---- Meeting Intelligence (happiness + relationship ledger) -----------------

  /**
   * Load the client-sentiment rollup (recent happiness scores). Find returns a
   * bare array (custom ServiceInterface) keyed by clientId; we keep the single
   * row for THIS client. Degrades silently — no chip renders without data.
   */
  async loadHappiness() {
    if (!this.clientId) return;
    this.setSectionError("happiness", null);
    try {
      const result = await findOrThrow(this.clientSentimentService, {
        query: { clientId: this.clientId },
      });
      const rows = rowsOf<ClientSentiment>(
        result as unknown as FindResult<ClientSentiment>
      );
      runInAction(() => {
        this.happiness =
          rows.find((r) => String(r.clientId) === this.clientId) ||
          rows[0] ||
          null;
      });
    } catch (err) {
      console.error("Failed to load client happiness:", err);
      this.setSectionError("happiness", err, "The client's sentiment could not be loaded.");
    }
  }

  /**
   * Load this client's relationship ledger — the expectation/need/commitment
   * items where the client is the subject (holder). Newest first; soft-deleted
   * rows are filtered out in {@link relationshipItemsByKind}.
   */
  async loadRelationshipItems() {
    if (!this.clientId) return;
    this.relationshipLoading = true;
    this.setSectionError("relationship", null);
    try {
      const result = await findOrThrow(this.relationshipItemsService, {
        query: {
          subjectType: "client",
          subjectId: this.clientId,
          $limit: 200,
          $sort: { createdAt: -1 },
        },
      });
      runInAction(() => {
        this.relationshipItems = rowsOf<RelationshipItem>(
          result as unknown as FindResult<RelationshipItem>
        );
      });
    } catch (err) {
      console.error("Failed to load relationship ledger:", err);
      this.setSectionError("relationship", err, "The relationship ledger could not be loaded.");
    } finally {
      runInAction(() => {
        this.relationshipLoading = false;
      });
    }
  }

  /** Live (non-deleted) ledger items, newest first. */
  get liveRelationshipItems(): RelationshipItem[] {
    return this.relationshipItems.filter((r) => !r.deletedAt);
  }

  /** Open ledger items grouped by kind (expectation/need/commitment). */
  get relationshipItemsByKind(): Record<string, RelationshipItem[]> {
    const groups: Record<string, RelationshipItem[]> = {};
    for (const item of this.liveRelationshipItems) {
      const key = item.kind || "other";
      (groups[key] ||= []).push(item);
    }
    return groups;
  }

  /** Count of still-open ledger items (drives the tab badge). */
  get openRelationshipCount(): number {
    return this.liveRelationshipItems.filter((r) => r.status === "open").length;
  }

  /** operator+ — mark a ledger item resolved (or reopen it). */
  async setRelationshipItemStatus(
    id: number,
    status: "open" | "resolved"
  ): Promise<boolean> {
    if (!this.canWrite) return false;
    try {
      await this.relationshipItemsService?.patch(id, { status });
      await this.loadRelationshipItems();
      return true;
    } catch (err) {
      console.error("Failed to update relationship item:", err);
      return false;
    }
  }

  /** operator+ — retire a ledger item (soft delete). */
  async removeRelationshipItem(id: number): Promise<boolean> {
    if (!this.canWrite) return false;
    try {
      await this.relationshipItemsService?.remove(id);
      await this.loadRelationshipItems();
      return true;
    } catch (err) {
      console.error("Failed to remove relationship item:", err);
      return false;
    }
  }

  // ---- Comms: uploaded transcripts + Outlook email threads --------------------

  /** Load the uploaded email threads attributed to this client (newest first). */
  async loadEmailMessages() {
    if (!this.clientId) return;
    this.emailMessagesLoading = true;
    this.setSectionError("emailMessages", null);
    try {
      const result = await findOrThrow(this.emailMessagesService, {
        query: {
          clientId: this.clientId,
          $limit: EMAIL_MESSAGES_LIMIT,
          $sort: { createdAt: -1 },
        },
      });
      runInAction(() => {
        this.emailMessages = rowsOf<EmailMessageRow>(
          result as unknown as FindResult<EmailMessageRow>
        );
      });
    } catch (err) {
      console.error("Failed to load email threads:", err);
      this.setSectionError("emailMessages", err, "This client's email threads could not be loaded.");
    } finally {
      runInAction(() => {
        this.emailMessagesLoading = false;
      });
    }
  }

  clearUploadState() {
    this.uploadError = null;
    this.uploadNotice = null;
  }

  /**
   * POST a raw file body to one of the Meeting Intelligence upload endpoints
   * (mirrors uploadDocument): Bearer auth + X-File-Name, sent as octet-stream so
   * koa-body never truncates a large transcript. Returns the parsed JSON body.
   */
  private async postUpload(
    ended: () => boolean,
    path: string,
    file: File,
    extraQuery: Record<string, string> = {}
  ): Promise<Record<string, unknown> | null> {
    if (!this.clientId) return null;
    const clientId = this.clientId;
    const { env } = await import("../../../config/environment");
    // A raw fetch the session guard never sees: past a sign-out this ends the
    // way a guarded call would, so the caller's catch stays quiet.
    if (ended()) throw new SessionEndedError(path);
    const token = this.rootStore.authStore?.token ?? null;
    const query = new URLSearchParams({ clientId, ...extraQuery });
    const headers: Record<string, string> = {
      // octet-stream lifts koa-body's text-body size limit for big transcripts.
      "Content-Type": "application/octet-stream",
      "X-File-Name": encodeURIComponent(file.name),
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${env.apiUrl}${path}?${query}`, {
      method: "POST",
      headers,
      body: file,
    });
    if (ended()) throw new SessionEndedError(path);
    if (!response.ok) {
      const text = await response.text();
      let message = `Upload failed (${response.status})`;
      try {
        const parsed = JSON.parse(text) as { message?: unknown };
        if (typeof parsed.message === "string" && parsed.message) message = parsed.message;
      } catch {
        if (text) message = text;
      }
      throw new Error(message);
    }
    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    if (ended()) throw new SessionEndedError(path);
    return body;
  }

  /**
   * Upload a transcript file (.txt/.vtt/.srt/.json) for this client. Creates a
   * manual meeting, stores the original, and runs the intelligence pipeline.
   * Returns the new meetingId (for navigation) or null on failure.
   */
  async uploadTranscript(file: File, title?: string): Promise<number | null> {
    if (!this.clientId) return null;
    this.transcriptUploading = true;
    this.uploadError = null;
    this.uploadNotice = null;
    const ended = sessionMark(this.rootStore);
    try {
      const extra: Record<string, string> = {};
      if (title?.trim()) extra.title = title.trim();
      const body = await this.postUpload(ended, "/meeting-uploads/transcript", file, extra);
      if (ended()) return null;
      const meetingId = typeof body?.meetingId === "number" ? body.meetingId : null;
      runInAction(() => {
        this.uploadNotice = "Transcript uploaded — analysis is running.";
      });
      return meetingId;
    } catch (err: unknown) {
      console.error("uploadTranscript failed:", err);
      if (isSessionEnded(err) || ended()) return null;
      runInAction(() => {
        this.uploadError =
          err instanceof Error ? err.message : "Failed to upload transcript";
      });
      return null;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.transcriptUploading = false;
        });
      }
    }
  }

  /**
   * Upload an Outlook .msg/.eml email thread for this client. Archives the
   * original + attachments, records the thread, and runs intelligence. Reloads
   * the email list on success.
   */
  async uploadEmail(file: File): Promise<boolean> {
    if (!this.clientId) return false;
    this.emailUploading = true;
    this.uploadError = null;
    this.uploadNotice = null;
    const ended = sessionMark(this.rootStore);
    try {
      const body = await this.postUpload(ended, "/meeting-uploads/email", file);
      if (ended()) return false;
      const attachments =
        typeof body?.attachmentCount === "number" ? body.attachmentCount : 0;
      runInAction(() => {
        this.uploadNotice =
          `Email archived${attachments ? ` with ${attachments} attachment${attachments === 1 ? "" : "s"}` : ""}` +
          " — analysis is running.";
      });
      await this.loadEmailMessages();
      return !ended();
    } catch (err: unknown) {
      console.error("uploadEmail failed:", err);
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.uploadError = err instanceof Error ? err.message : "Failed to upload email";
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.emailUploading = false;
        });
      }
    }
  }

  /**
   * Download a stored document by id through the authenticated content endpoint
   * (the storage key is never exposed; a plain <a href> would 401). Used for an
   * email's original file + its attachments.
   */
  async downloadDocumentById(documentId: string, fileName?: string): Promise<boolean> {
    return this.downloadStoredDocument(documentId, fileName || "download", "uploadError", "Failed to download file");
  }

  /**
   * The documents attached to an uploaded email's meeting (original + each
   * attachment), fetched on demand for the expand row. Returns [] on failure.
   */
  async loadEmailDocuments(meetingId: number): Promise<ClientDocumentRecord[]> {
    try {
      const result = await this.documentsService?.find({
        query: {
          entityType: "meeting",
          entityId: String(meetingId),
          $limit: 100,
          $sort: { createdAt: 1 },
        },
      });
      return rowsOf<ClientDocumentRecord>(
        result as unknown as FindResult<ClientDocumentRecord>
      );
    } catch (err) {
      console.error("Failed to load email documents:", err);
      return [];
    }
  }

  // ---- Onboarding stepper (OverviewTab; operator+) ----------------------------

  /**
   * Persist a new onboarding step (and completion when the step is 'live').
   *
   * THROW_ON_ERROR: the ServiceStore otherwise RESOLVES a refused patch (the
   * client write gate, a role) as null, and the stepper read it as saved — the
   * click looked to work and the stage had not moved.
   */
  async setOnboardingStep(step: ClientOnboardingStepKey): Promise<boolean> {
    if (!this.clientId || !this.canWrite) return false;
    this.onboardingSaving = true;
    this.onboardingError = null;
    try {
      const completed = step === "live";
      const payload: Record<string, unknown> = {
        onboardingStep: step,
        onboardingCompleted: completed,
        onboardingCompletedAt: completed ? new Date().toISOString() : null,
      };
      const updated = (await this.clientsService?.patch(
        this.clientId,
        payload,
        THROW_ON_ERROR
      )) as unknown as ClientRecord | undefined;
      runInAction(() => {
        if (updated) this.client = updated;
      });
      return true;
    } catch (err) {
      console.error("Failed to update onboarding step:", err);
      runInAction(() => {
        if (!isSessionEnded(err)) this.onboardingError = errorMessage(err, "The onboarding stage could not be changed.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.onboardingSaving = false;
      });
    }
  }

  // ---- Archive / restore -------------------------------------------------------

  /**
   * Archive (soft-delete) this client. The API converts remove into a
   * `deletedAt` patch (operator+). Returns true so the header can navigate
   * back to the list.
   */
  async archiveClient(): Promise<boolean> {
    if (!this.clientId || !this.canWrite) return false;
    this.archiveBusy = true;
    this.archiveError = null;
    try {
      // THROW_ON_ERROR: the ServiceStore otherwise RESOLVES a refusal as null,
      // which read as archived — the confirm closed and a non-admin was sent
      // back to the list while the client was still live.
      const archived = (await this.clientsService?.remove(
        this.clientId,
        THROW_ON_ERROR
      )) as unknown as ClientRecord | undefined;
      runInAction(() => {
        if (archived) this.client = archived;
      });
      return true;
    } catch (err) {
      console.error("Failed to archive client:", err);
      runInAction(() => {
        if (!isSessionEnded(err)) this.archiveError = errorMessage(err, "The client could not be archived.");
      });
      return false;
    } finally {
      runInAction(() => {
        this.archiveBusy = false;
      });
    }
  }

  clearArchiveError() {
    this.archiveError = null;
  }

  /** Restore an archived client (admin only — API re-enforces). */
  async restoreClient(): Promise<boolean> {
    if (!this.clientId || !this.isAdminTier) return false;
    this.archiveBusy = true;
    try {
      const restored = (await this.clientsService?.patch(this.clientId, {
        deletedAt: null,
      })) as unknown as ClientRecord | undefined;
      runInAction(() => {
        if (restored) this.client = restored;
      });
      return true;
    } catch (err) {
      console.error("Failed to restore client:", err);
      return false;
    } finally {
      runInAction(() => {
        this.archiveBusy = false;
      });
    }
  }

  // ---- Realtime (clients / client-modules / client-tools / client-contacts) ----

  /**
   * Subscribe the open client to live CRUD events (shared refcounted socket —
   * see clientsRealtime.ts). Idempotent per store; replaced when the clientId
   * changes and torn down by the route onExit via {@link disposeRealtime}.
   */
  subscribeRealtime() {
    if (this.realtimeDisposer) return;
    // Shared realtime bus (src/realtime): ONE connection for the whole app,
    // refcounted there. The JWT is sourced internally from the AuthStore —
    // there is no token argument to forget.
    this.realtimeDisposer = subscribeClientEvents(this.rootStore, (event) =>
      this.handleRealtimeEvent(event),
    );
    // What the meeting pipeline writes straight to its tables emits no CRUD
    // event; its "analysed" announcement names the client instead.
    this.meetingEventsDisposer = subscribeClientMeetingEvents(this.rootStore, (event) =>
      this.handleClientMeetingEvent(event),
    );
    // The Deliverables "Tasks" chip counts assignments; their notices (#3820).
    this.workEventsDisposer = subscribeClientWorkEvents(this.rootStore, (event) =>
      this.handleClientWorkEvent(event),
    );
  }

  /** Route onExit — tear down the live subscriptions. */
  disposeRealtime() {
    if (this.realtimeDisposer) {
      this.realtimeDisposer();
      this.realtimeDisposer = null;
    }
    if (this.meetingEventsDisposer) {
      this.meetingEventsDisposer();
      this.meetingEventsDisposer = null;
    }
    if (this.workEventsDisposer) {
      this.workEventsDisposer();
      this.workEventsDisposer = null;
    }
    // A re-count still waiting would land on whatever is open next.
    this.taskCountsSoon.cancel();
  }

  /**
   * A meeting of the OPEN client moved through the pipeline. Its relationship
   * signals, its mapping and the assignments distribution creates are written
   * by the pipeline directly (no CRUD event for any of them), so the sections
   * they feed are read again — each on the announcement that says its rows
   * exist:
   *  - `meeting-analyzed` (Gantry's push, the default): everything is stored —
   *    the ledger, the meetings and the Deliverables chip;
   *  - `meeting-processed` (HQ's own pass, the rollback): the meeting and its
   *    items — the meetings, and the ledger as before (its signals follow);
   *  - `meeting-enriched` (that pass's signals and analyses written, #3820):
   *    the ledger and the meetings. Before it existed this case missed them;
   *  - `meeting-distributed` (a run routed the items, on any path): the chip;
   *  - `meeting-mapped` (an operator mapped a meeting to a client, 2026-11-02):
   *    for the client it joined AND the one it left, the meetings and the chip
   *    — the work it had moved with it (`migrateInternalEraWork`), and none of
   *    that work was in the last count's read, so a later notice about it
   *    would not have matched.
   */
  private handleClientMeetingEvent(event: ClientMeetingAnalysedEvent) {
    const clientId = this.clientId;
    if (!clientId) return;
    if (event.kind === "meeting-mapped") {
      if (!sameClientId(event.clientId, clientId) && !sameClientId(event.previousClientId, clientId)) return;
      void this.loadMeetings();
      this.taskCountsSoon();
      return;
    }
    if (!sameClientId(event.clientId, clientId)) return;
    if (event.kind !== "meeting-distributed") {
      void this.loadRelationshipItems();
      void this.loadMeetings();
    }
    if (event.kind === "meeting-analyzed" || event.kind === "meeting-distributed") this.taskCountsSoon();
  }

  /**
   * An assignment's work moved somewhere in HQ (#3820). The notice names the
   * assignment and never its client, so it counts here when this client's
   * last count read that assignment, when a module move names one of this
   * client's modules, or when a retarget names this client (it took the work
   * here, or away). Then the counts are read again — the server counts, never
   * this page.
   */
  private handleClientWorkEvent(event: ClientWorkEvent) {
    const clientId = this.clientId;
    if (!clientId) return;
    const counted = event.assignmentId !== null && this.taskCountAssignmentIds.has(event.assignmentId);
    const ourModule = event.moduleIds.some((id) => this.modules.some((m) => String(m.id) === id));
    const ourClient = event.clientIds.some((id) => sameClientId(id, clientId));
    if (counted || ourModule || ourClient) this.taskCountsSoon();
  }

  /**
   * Apply a live CRUD event scoped to the OPEN clientId, updating rows in
   * place (no full reload). Events for other clients are ignored.
   *
   * The tabs hold their own copies of these rows (loaded per client), so an
   * event that is not applied HERE never reaches the screen, whatever the
   * ServiceStore underneath merged: that is how the Whiteboard, Activity and
   * Relationship tabs stayed stale until a refresh (#3820). A row belongs to
   * the open client by one of three keys — `clientId` (the client-* tables,
   * communications, whiteboard lanes), the relationship ledger's subject, or,
   * for a week note, the lane it sits on (a cell carries no clientId).
   */
  private handleRealtimeEvent(event: ClientServiceEvent) {
    if (!this.clientId) return;
    const clientId = this.clientId;
    const record = event.record;

    if (event.service === "clients") {
      // Only the open client's own row matters here.
      if (String(record.id) !== clientId) return;
      if (event.action === "patched" || event.action === "updated") {
        runInAction(() => {
          this.client = { ...(this.client || {}), ...record } as ClientRecord;
        });
      }
      return;
    }

    const ownedByClient = String(record.clientId ?? "") === clientId;
    // A payload that does not CARRY a key says nothing about it: a write made
    // with `$select` publishes only the fields selected, and reading a missing
    // key as "not this client's" dropped a row that still is.
    const has = (key: string) => Object.prototype.hasOwnProperty.call(record, key);
    const isListed = <T extends { id: string | number }>(rows: T[]) =>
      rows.some((r) => String(r.id) === String(record.id));
    const membershipBy = (carried: boolean, belongs: boolean): LiveMembership =>
      !carried ? "unknown" : belongs ? "here" : "elsewhere";
    const byClient = membershipBy(has("clientId"), ownedByClient);

    if (event.service === "whiteboard-rows") {
      // A lane re-pointed AT this client arrives as a patch of a row this tab
      // never loaded, and its week notes with it: read the section again.
      if (
        byClient === "here" &&
        !isListed(this.whiteboardRows) &&
        event.action !== "created" &&
        event.action !== "removed" &&
        !record.deletedAt
      ) {
        void this.loadWhiteboard();
        return;
      }
      runInAction(() => {
        this.whiteboardRows = applyLiveEvent(this.whiteboardRows, event, byClient);
        // A lane that left (archived, or moved to another client) takes its notes.
        const lanes = new Set(this.whiteboardRows.map((r) => String(r.id)));
        if (this.whiteboardCells.some((c) => !lanes.has(String(c.rowId)))) {
          this.whiteboardCells = this.whiteboardCells.filter((c) => lanes.has(String(c.rowId)));
        }
      });
      return;
    }

    if (event.service === "whiteboard-cells") {
      const onOurLane = this.whiteboardRows.some((r) => String(r.id) === String(record.rowId ?? ""));
      runInAction(() => {
        this.whiteboardCells = applyLiveEvent(this.whiteboardCells, event, membershipBy(has("rowId"), onOurLane));
      });
      return;
    }

    if (event.service === "relationship-items") {
      const aboutClient = record.subjectType === "client" && String(record.subjectId ?? "") === clientId;
      const membership = membershipBy(has("subjectType") || has("subjectId"), aboutClient);
      runInAction(() => {
        // The ledger is read newest first.
        this.relationshipItems = applyLiveEvent(this.relationshipItems, event, membership, "start");
      });
      return;
    }

    // Child collections — scoped by the record's clientId. A row moved to
    // another client leaves this one's tab, and appears on the other's.
    runInAction(() => {
      if (event.service === "client-modules") {
        this.modules = applyLiveEvent(this.modules, event, byClient);
      } else if (event.service === "client-tools") {
        this.tools = applyLiveEvent(this.tools, event, byClient);
      } else if (event.service === "client-contacts") {
        this.contacts = applyLiveEvent(this.contacts, event, byClient);
      } else if (event.service === "communications") {
        // The Activity timeline sorts by date, so where a note lands is moot.
        this.communications = applyLiveEvent(this.communications, event, byClient);
      }
    });
  }

  // ---- Client instances (InstancesTab) ------------------------------------

  /** This client's instances ordered api→web (the two containers). */
  get orderedInstances(): ClientInstance[] {
    const roleRank = (role: string) => {
      const idx = CLIENT_INSTANCE_ROLE_ORDER.indexOf(role);
      return idx === -1 ? CLIENT_INSTANCE_ROLE_ORDER.length : idx;
    };
    return [...this.instances].sort((a, b) => roleRank(a.role) - roleRank(b.role));
  }

  get instanceCount(): number {
    return this.instances.length;
  }

  get runningInstanceCount(): number {
    return this.instances.filter((i) => isClientInstanceRunning(i.status)).length;
  }

  /** How many of this client's instances have a newer image available (Phase 3). */
  get updatesAvailableCount(): number {
    return this.instances.filter((i) => i.updateAvailable === true).length;
  }

  /** True when at least one instance can be updated to a newer image. */
  get hasInstanceUpdates(): boolean {
    return this.updatesAvailableCount > 0;
  }

  /**
   * Load THIS client's deployed containers (scoped by clientId). Read-only:
   * status/image/digest are reported by the API from the live Portainer host.
   * No secrets are read or surfaced.
   */
  async loadInstances() {
    if (!this.clientId) return;
    this.instancesLoading = true;
    this.setSectionError("instances", null);
    try {
      // NO `$limit`. client-instances now honours paging (H6), so the old
      // `$limit: 50` became a real, silent cap on this tab; a client's
      // instances are a short list, and without paging params the API answers
      // with all of them — the same read the Instances grid makes.
      const result = await findOrThrow(this.clientInstancesService, {
        query: {
          clientId: this.clientId,
          $sort: { role: 1 },
        },
      });
      runInAction(() => {
        // client-instances may serialise paginated or bare; rowsOf handles both.
        this.instances = rowsOf<ClientInstance>(result as unknown as FindResult<ClientInstance>);
      });
    } catch (err) {
      console.error("Failed to load client instances:", err);
      this.setSectionError("instances", err, "This client's instances could not be loaded.");
    } finally {
      runInAction(() => {
        this.instancesLoading = false;
      });
    }
  }

  // ---- Instance actions (Phase 5, InstancesTab) ---------------------------
  // operator+ Start/Stop/Restart/Deploy/Rollback against THIS client's two
  // containers. The shared InstanceActionsStore drives the Deploy/Rollback/
  // confirm DIALOGS; this store owns the actual transport + optimistic refresh
  // so the tab's rows update in place. No secret is sent — the API proxies to
  // Portainer with the host's encrypted key and writes the audit row.

  /**
   * operator+ on infrastructure, AND allowed for this client (its containers
   * are its records) — whether the per-row action buttons render on the tab.
   */
  get canManageInstances(): boolean {
    return canManageInfra(this.rootStore) && this.clientEditAllowed !== false;
  }

  isInstanceActioning(instanceId: string): boolean {
    return this.instanceActioningIds.includes(instanceId);
  }

  clearInstanceActionError() {
    this.instanceActionError = null;
  }

  private beginInstanceAction(instanceId: string) {
    if (!this.instanceActioningIds.includes(instanceId)) {
      this.instanceActioningIds = [...this.instanceActioningIds, instanceId];
    }
    this.instanceActionError = null;
  }

  private endInstanceAction(instanceId: string) {
    this.instanceActioningIds = this.instanceActioningIds.filter(
      (id) => id !== instanceId
    );
  }

  private optimisticInstanceStatus(
    action: InstanceActionType
  ): ClientInstanceStatus | null {
    switch (action) {
      case "start":
        return "starting";
      case "restart":
        return "restarting";
      case "stop":
        return "stopped";
      default:
        return null;
    }
  }

  /** Run a Start / Stop / Restart against one of this client's instances. */
  async runInstanceLifecycle(
    instance: ClientInstance,
    action: InstanceActionType,
    /** The confirm dialog's progress token (InstanceActionsStore.runConfirmed). */
    progressToken?: string
  ): Promise<boolean> {
    if (!this.canManageInstances) return false;
    this.beginInstanceAction(instance.id);
    const optimistic = this.optimisticInstanceStatus(action);
    if (optimistic) {
      runInAction(() => {
        this.instances = this.instances.map((i) =>
          i.id === instance.id ? { ...i, status: optimistic } : i
        );
      });
    }
    try {
      // Routes through the `instance-actions` ServiceStore (create-only); the
      // helper is owned by the infrastructure domain.
      const result = await runInstanceAction(this.instanceActionsService, {
        action,
        instanceId: instance.id,
        ...(progressToken ? { progressToken } : {}),
      });
      // The container serving HQ is handed to a helper: follow it.
      followHandOff(this.rootStore, result, instance);
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      console.error(`Instance ${action} failed:`, err);
      // The sign-out ended it: no error, and no re-read in the next session
      // (the optimistic status went with the purged store).
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.instanceActionError =
          err instanceof Error ? err.message : `Failed to ${action} instance`;
      });
      await this.loadInstances();
      return false;
    } finally {
      runInAction(() => this.endInstanceAction(instance.id));
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.moduleFormError = null;
    this.toolFormError = null;
    this.contactFormError = null;
    this.archiveError = null;
    this.devopsLinkActionError = null;
    this.deliverableActionError = null;
    this.contactActionError = null;
    this.disposeRealtime();
    this.clientId = null;
    this.loading = false;
    this.client = null;
    this.loadError = null;
    this.notFound = false;
    this.sectionErrors = emptySectionState<string | null>(null);
    this.modules = [];
    this.tools = [];
    this.modulesLoading = false;
    this.toolsLoading = false;
    this.moduleForm = {};
    this.editingModuleId = null;
    this.moduleSaving = false;
    this.moduleCatalog = [];
    this.moduleCatalogError = null;
    this.moduleCatalogInFlight = null;
    this.moduleCatalogLoaded = false;
    this.moduleTaskCounts = {};
    this.taskCountAssignmentIds = new Set();
    // A count still out lands nowhere: it is no longer the newest.
    this.taskCountsSeq++;
    this.toolForm = {};
    this.editingToolId = null;
    this.toolSaving = false;
    this.devopsLinks = [];
    this.devopsLinksLoading = false;
    this.devopsConnections = [];
    this.devopsProjects = [];
    this.devopsRepos = [];
    this.devopsPipelines = [];
    this.devopsProjectsLoading = false;
    this.devopsEntitiesLoading = false;
    this.devopsLinkForm = {};
    this.devopsLinkSaving = false;
    this.editingDevOpsLinkId = null;
    this.devopsLinkError = null;
    this.devopsLinkSubscriptionErrors = [];
    this.contacts = [];
    this.contactsLoading = false;
    this.contactForm = {};
    this.editingContactId = null;
    this.contactSaving = false;
    this.communications = [];
    this.auditEntries = [];
    this.devopsEvents = [];
    this.activityLoading = false;
    this.activityPage = 1;
    this.noteSubject = "";
    this.noteSummary = "";
    this.noteSaving = false;
    this.documents = [];
    this.documentsLoading = false;
    this.documentUploading = false;
    this.documentError = null;
    this.summary = null;
    this.health = null;
    this.healthHistory = [];
    this.invoices = [];
    this.invoicesLoading = false;
    this.billingEnabled = null;
    this.meetings = [];
    this.meetingsLoading = false;
    this.whiteboardRows = [];
    this.whiteboardCells = [];
    this.whiteboardLoading = false;
    this.onboardingSaving = false;
    this.onboardingError = null;
    this.archiveBusy = false;
    this.devopsLinkStatuses = {};
    this.realtimeDisposer = null;
    this.meetingEventsDisposer = null;
    this.workEventsDisposer = null;
    this.instances = [];
    this.instancesLoading = false;
    this.instanceActioningIds = [];
    this.instanceActionError = null;
    this.happiness = null;
    this.relationshipItems = [];
    this.relationshipLoading = false;
    this.emailMessages = [];
    this.emailMessagesLoading = false;
    this.transcriptUploading = false;
    this.emailUploading = false;
    this.uploadError = null;
    this.uploadNotice = null;
    this.commsSearch = "";
    this.commsSortKey = "sentAt";
    this.commsSortDir = "desc";
    this.docsSearch = "";
    this.docsCategoryFilter = "all";
    this.docsSortKey = "uploadedAt";
    this.docsSortDir = "desc";
    this.deliverablesTypeFilter = "all";
    this.deliverablesSortKey = "kind";
    this.deliverablesSortDir = "asc";
    this.lastTabBySection = {};
  }
}

export default ClientDetailStore;
